import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Ruteo from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Lock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import GroupsLoadError from '../components/GroupsLoadError';
import PanelChatCajon from '../components/PanelChatCajon';
import StickyBackPill, { PILL_BUTTON_CLASS } from '../components/StickyBackPill';
import { fechaDeCaducidadDe, formatearFechaDeCaducidad, zonaDelNavegador } from '../lib/fechaDeCaducidad';
import { ACTION_BAR_BOTTOM_CLASS } from '../lib/bottomNavMetrics';
import { esAngosto, useAnchoDeCaja } from '../hooks/useAnchoDeCaja';
import { CONSULTA_ANCHO, useMedia } from '../lib/useEsAncho';
import {
  HUECO_DE_LA_BARRA, altoDelTelefonoDelRecuerdo, barraRecordada, botonDelRecuerdo, cabeLaBarraDeComputadora,
  useMedidasDeLaBarra,
} from '../lib/disposicionDeLaBarra';
import { conCortesSuaves, useFlotanteQueEspera } from '../lib/telefonoDelAvance';
// post-t7-2c (D2): LAPIDA de `altoReservadoDeLaBarra` y `chatRecordado` aqui (ver `altoDeLaBarraDelEsqueleto`).
import {
  altoDeLaBarraDeComputadora, MEDIDAS_DE_LA_BARRA_DE_COMPUTADORA, recordarChat, recordarDelGrupo, recuerdoDelGrupo,
} from '../lib/esqueletoDelAvance';
import { HUECOS_DE_LA_BARRA } from '../lib/disposicionDeLaBarra';
import { ESTILO_DE_LA_BARRA } from '../lib/estiloDeLaTablaDelAvance';
// dashboard-rediseno (4.1): la barra pegajosa se pega al borde del contenido con el relleno de la fuente unica del chasis.
import { PEGAJOSA_A_LO_ANCHO_CLASS } from '../lib/medidasDelChasis';
import {
  CAPA_DE_SECCION, CABECERA_DE_SECCION, CHIP_DE_CONTEO, CUERPO_DE_SECCION, GUIA_DE_SECCION,
  TITULO_DE_SECCION_PLEGABLE,
} from '../lib/vestidoDelCarril';
import {
  AMARILLO, BORDE_POCO, BORDE_SIN_PRACTICAR, COLOR_DEL_ESTADO, ESTADOS, ESTADOS_DE_LA_CARD, GRIS_DE_LAS_BARRAS, NARANJA,
  RENGLONES_MEJORARON, atenuado,
  RIEL, ROJO, TEXTO_SOBRE_EL_ESTADO, TRAMO_POCO, TRAMO_VA_BIEN, VERDE, anchoDeConteo, chipDeCaduca, cuandoDeDias,
  cuandoTexto, horaLocal, lineasDeConteos, MARCA_DE_MOTIVOS, unirNombres, listaDeNombres, msHastaLaMedianoche, negritasXdeY, seUnioTexto, sinAcentos,
  temaDeLaCard,
  textoDelCuadro, tramos, tramosDeLaBarrita,
} from '../lib/avanceDelGrupo';

/* t7-2d (#542 a #547 y #549, diseno v6 secciones 1, 2.3, 2.4, 2.11, 2.13, 2.14 y 3) -- LA PORTADA V3
   DE "AVANCE DEL GRUPO". (La cara v2 era de t7-2c, #532.)

   DONDE VIVE: ruta `/groups/:groupId/avance`, dentro de `AppLayout` y detras de
   `InstitutionalRoute`. Se registra en los dos builds, como sus hermanas.

   UNA SOLA LLAMADA: `GET /api/grupos/{id}/avance?lang=<idioma de i18n>&tz=<zona del navegador>`
   al montar y al volver a la pestana; sin sondeo. Todo numero llega hecho
   (`services/pipe_inst/avance.py::calcular_con_mejora`); la pagina solo formatea con el reloj
   y el idioma del NAVEGADOR (lib/avanceDelGrupo.js).

   LA PORTADA contesta tres preguntas: quien esta en riesgo (el total y un renglon por motivo con
   el mas urgente), que no estan entendiendo (el tema con su pregunta A LA VISTA, "Ver como
   explicarla", los otros temas en rojo y, sin rojos, la cascada) y va mejorando (dos pares de
   barras y quienes mejoraron). Debajo, los avisos de card; plegados, Por card, Por alumno (por
   motivo, con buscador y "Recordar a todos ellos") y Tabla por card. Con 3 alumnos o menos la
   portada dice nombres en vez de "x de y". La leyenda de colores dice, al tocarla, que hacer con
   cada uno. Un conteo de personas en cero se escribe "nadie" o no se pinta.

   LAPIDA t7-2d: murieron `Kpi` (la flecha y el "eran n"), los grupos "Nunca han practicado" y
   "Practicando" de "Por alumno", "Ver por card" (lo sustituye "Ver todos los temas"), "Ningun tema
   esta en rojo o amarillo." (lo sustituye la cascada) y "{{s}} practicas" del detalle (lo
   sustituye "Practico en {{s}} dias distintos").

   LAS MEDIDAS EXACTAS DEL DISENO van en `style` y no en clases nuevas: la hoja de Tailwind solo
   gana lo que el acomodo responsivo necesita (reporte, seccion Tailwind). */

/** Un valor de CSS de varias piezas (la guarda del perimetro lee una cadena con espacios como
    una frase sin traducir). */
const css = (...piezas) => piezas.join(' ');
const NADA = () => {};
/** t7-3 remate 4 (C4): la flotante donde no hay barra de navegacion abajo (768 o mas): a 12 px del borde. */
/** t7-3 remate 4 (D3): cuanto se encima un circulito de iniciales sobre el anterior en "Por publicacion". El
    solape solo puede ocupar la franja vacia de la orilla: circulo de 26, letra de 10 en negritas y las iniciales
    mas anchas ("MW") de ~18 px dejan 4 px por lado; con 3 ninguna letra queda bajo otro circulo (antes, 8: "MO"
    se leia "MC"). */
export const SOLAPE_DE_LAS_CARAS = 3;
/** t7-3 remate 7: el aire al final de la pagina cuando la flotante esta a la vista (44 de la pastilla, 12 de su margen
    y 16 de aire). */
export const AIRE_BAJO_LA_FLOTANTE = 72;
export const FLOTANTE_SIN_BARRA_DE_ABAJO = { bottom: css('calc(12px', '+', 'env(safe-area-inset-bottom,', '0px))'), marginBottom: 0 };
const borde = (color, ancho = 1) => ({ borderWidth: ancho, borderStyle: 'solid', borderColor: color });
const relleno = (v, h) => ({ paddingTop: v, paddingBottom: v, paddingLeft: h, paddingRight: h });
const GRIS = '#a0a0a0';
// t7-3 remate 7 (regla 2 de #581): murio el gris de texto #6a6a6a (contraste 3.3 a 1, ilegible); todo texto que lo
// usaba ("caducada", "acierta", "Recordado hoy", "Reconocelos en clase.", la nota de la tabla, la de pocos) va en GRIS
const BLANCO = '#f2f2f2';
const TARJETA = { background: '#181818', ...borde('#262626'), borderRadius: 12 };
const CAJA = { background: '#111111', ...borde('#262626'), borderRadius: 10 };
const CAPA = { background: '#1a1210', ...borde('#33261d'), borderRadius: 16, padding: 16, minWidth: 0 };
const PASTILLA = { borderColor: NARANJA, backgroundColor: 'rgba(255,107,0,0.10)', color: NARANJA };
// LAPIDA t7-3 remate 2 (R2): murio `TRES_RENGLONES` (la pregunta a la vista ya no se recorta).
// LAPIDA t7-3 remate 3 (B4, #568): murio `DOS_RENGLONES` (las pestanas y las cabeceras del Resumen del telefono
// ya no se recortan: parten renglon).
const UN_RENGLON = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 };
/** Remate de post-t7-2c (decision de Carlos del 15-oct): el nombre del grupo en el boton de regresar, en la disposicion
    del TELEFONO, parte renglon hasta DOS renglones y solo despues se corta con puntos suspensivos (la caja de webkit:
    `line-clamp` es inerte junto a un `display` de bloque). Parte entre palabras; una palabra larguisima parte solo si no
    cabe en el renglon (`break-word`, la regla C3 de la barra: nunca `anywhere`). */
export const NOMBRE_EN_DOS_RENGLONES = { display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
  overflow: 'hidden', overflowWrap: 'break-word', minWidth: 0 };
/** t7-3 remate 2 (R2, Carlos 30-sep: "por que truncas la pregunta?"): NADA del panel se recorta; un
    texto largo parte renglon (y una palabra larguisima tambien, sin salirse de su caja). Los unicos
    recortes que quedan, declarados: la barra fija (un renglon de navegacion), los renglones de 40 px
    de la lista de un motivo (5 a la vista), la publicacion bajo el tema (R5) y las pestanas de la tabla
    en el telefono (dos renglones). */
const PARTE_RENGLON = { overflowWrap: 'break-word', minWidth: 0 };
/** t7-3 remate 2 (R7): el texto de lectura larga de "Ver como explicarla": gris claro (contraste AA contra la
    tarjeta), 15 px e interlineado 1.5. */
const GRIS_CLARO = '#c9c9c9';
// t7-3 remate 7 (regla 2 de #581): parte renglon y encoge dentro de su caja (antes un texto largo se salia de la tarjeta)
const LECTURA = { fontSize: 15, lineHeight: 1.5, color: GRIS_CLARO, overflowWrap: 'break-word', minWidth: 0 };
/** Por debajo de este ancho de CONTENIDO los renglones se apilan (telefono, y 768 con el menu). */
export const UMBRAL_ANGOSTO = 640;
/** Por debajo de este ancho de CONTENIDO las tres respuestas van una debajo de otra. */
export const UMBRAL_TRES_COLUMNAS = 760;
/** El alto minimo de la barra fija (seccion 3.1) y el aire que queda entre ella y un ancla al bajar (R6). */
export const ALTO_DE_LA_BARRA = 56;
/** t7-3 remate 8 (#585): el boton de regresar de la barra. En la disposicion de computadora, el de siempre (36 de alto,
    letra de 13, relleno de 14); en la de telefono, el de la pildora de regreso de la casa (44 de alto como minimo,
    letra de 14, relleno lateral de 16). El renglon de abajo empieza en la vertical de la flecha: su sangria es el
    borde mas el relleno izquierdo del boton, salidos de AQUI. */
export const BOTON_VOLVER = {
  computadora: { alto: 36, letra: 13, relleno: 14, arriba: 7 },
  telefono: { alto: 44, letra: 14, relleno: 16, arriba: 9 },
  borde: 1,
};
export const SANGRIA_DEL_RENGLON_TELEFONO = BOTON_VOLVER.borde + BOTON_VOLVER.telefono.relleno;
/** post-t7-2b (D1): la letra, el relleno y el borde del boton de regresar en una disposicion; una sola fuente para el
    boton y para su medidor oculto (el que da su ancho natural a la regla de la barra). */
const estiloDelVolver = (boton) => ({ gap: 8, minHeight: boton.alto, paddingTop: boton.arriba, paddingBottom: boton.arriba,
  paddingLeft: boton.relleno, paddingRight: boton.relleno, ...borde('#262626', BOTON_VOLVER.borde),
  borderRadius: boton.alto / 2, color: BLANCO, fontSize: boton.letra, fontWeight: 600, background: '#141414' });
/** t7-3 remate 8 (#585): el alto de la barra del telefono con el boton de 44 y un renglon de nombre (medido en Chrome,
    320 a 430, ES y EN): el esqueleto lo reserva y al llegar los datos no brinca. */
export const ALTO_DE_LA_BARRA_TELEFONO = 86.5;
export const AIRE_BAJO_LA_BARRA = 8;
/** Con mas de estos alumnos, "Por alumno" lleva el campo "Buscar por nombre" (#547). */
export const BUSCADOR_DESDE = 10;
// t7-3 remate 8 (#589): el quinto chip, "Ha practicado poco": contorno gris de 2 px sobre el fondo de la tarjeta y letra
// clara, el dibujo de la etiqueta del estado "Ha practicado poco"
const COLOR_DEL_CHIP = { nunca: '#2a2a2a', dejo: '#2a2a2a', le_cuestan: ROJO, se_atoro: AMARILLO, poco: 'transparent' };
// t7-3 remate 8 (decision 16): letra casi negra sobre el rojo (antes blanca, 3.91 a 1)
const TEXTO_DEL_CHIP = { nunca: BLANCO, dejo: BLANCO, le_cuestan: '#111111', se_atoro: '#111111', poco: TEXTO_SOBRE_EL_ESTADO.poco };
/** t7-3 remate 8 (decision 16): la letra de todo chip rojo del panel. */
const LETRA_SOBRE_EL_ROJO = '#111111';
const BORDE_DEL_CHIP = { nunca: BORDE_SIN_PRACTICAR, dejo: BORDE_SIN_PRACTICAR, le_cuestan: ROJO, se_atoro: AMARILLO,
  poco: BORDE_POCO };
const ANCHO_DEL_BORDE_DEL_CHIP = { poco: 2 };

function idiomaDe(i18n) {
  return String(i18n?.language || 'es').toLowerCase().startsWith('en') ? 'en' : 'es';
}

/** Los comodines de la seccion T llevan nombres distintos en espanol y en ingles ("{{tema}}" /
    "{{topic}}"): se pasan los dos. */
function conAlias(p) {
  return {
    ...p, topic: p.tema, topic1: p.tema1, when: p.cuando, difficulty: p.dificultad, option: p.opcion,
    reason: p.razon, question: p.pregunta, date: p.fecha, time: p.hora, names: p.nombres, name: p.nombre,
    document: p.documento,
  };
}

/** Que no se dispare el toque de la tarjeta de afuera. */
const soloEste = (fn) => (e) => { e.stopPropagation(); fn(e); };

/** Una tarjeta entera que es boton: Enter y espacio la abren como un clic. */
function botonDe(alTocar, abierta) {
  return {
    role: 'button', tabIndex: 0, onClick: alTocar, 'aria-expanded': abierta,
    onKeyDown: (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alTocar(e); }
    },
  };
}

/* -- piezas --------------------------------------------------------------- */

function Cuadrito({ estado, lado = 10, testid, titulo }) {
  return (
    <span aria-hidden="true" data-testid={testid} data-estado={estado} title={titulo}
      style={{
        display: 'inline-block', width: lado, height: lado, borderRadius: 3,
        background: COLOR_DEL_ESTADO[estado],
        // t7-3 remate 7 (#582 A): "Sin practicar" en el gris nuevo, con su borde del mismo gris (antes #3a3a3a)
        ...(estado === 'S' ? borde(GRIS_DE_LAS_BARRAS) : {}),
        ...(estado === 'va_bien' ? borde(VERDE, 2) : {}),
        // t7-3 remate 3 (#569): "Ha practicado poco", contorno gris de 2 px sin relleno
        ...(estado === 'poco' ? borde(BORDE_POCO, 2) : {}), boxSizing: 'border-box', flexShrink: 0,
      }} />
  );
}

function Barra({ dist, alto = 8, testid, estados }) {
  const lista = tramos(dist, estados);
  return (
    // t7-3 remate 7 (#582 A): la pista es el gris nuevo y solo se ve sin tramos (los tramos llenan la barra; con la
    // pista detras, el contorno de "Ha practicado poco" se confundiria con "Sin practicar")
    <div className="flex overflow-hidden w-full" data-testid={testid}
      style={{ height: alto, borderRadius: alto / 2, background: lista.length ? 'transparent' : GRIS_DE_LAS_BARRAS }}>
      {lista.map(({ estado, n }) => (
        <span key={estado} data-estado={estado}
          style={{
            flexGrow: n, flexBasis: 0,
            // t7-3 remate 7 (#582 A): "Ha practicado poco" es contorno del gris nuevo, sin relleno (antes un tramo
            // relleno #6a6a6a); "Sin practicar", el gris nuevo
            background: estado === 'va_bien' ? TRAMO_VA_BIEN : (estado === 'poco' ? TRAMO_POCO : COLOR_DEL_ESTADO[estado]),
            ...(estado === 'S' ? borde(GRIS_DE_LAS_BARRAS) : {}),
            ...(estado === 'poco' ? borde(GRIS_DE_LAS_BARRAS, 2) : {}),
            ...(estado === 'va_bien' ? borde(VERDE) : {}), boxSizing: 'border-box',
          }} />
      ))}
    </div>
  );
}

/** #549: la leyenda de los colores; cada color es un boton (32 px) que abre una burbuja con la accion de ese color.
    Una sola abierta; la cierran Esc, tocar fuera y abrir otra. post-t7-1 (#595): SEIS, en el orden de la casa (Domina,
    Va bien, A medias, Le cuesta, Ha practicado poco, Sin practicar); antes cinco, y "Ha practicado poco" vivia suelto
    arriba de la tabla. */
/* post-t7-2b (D1): la leyenda con 28 px menos de aire, sin tocar una letra: relleno de 3 + 3 por boton (antes 4 + 4),
   hueco de 4 entre el cuadrito y el rotulo (antes 5) y columnGap de 2 entre botones (antes 4). Los botones siguen de 32
   de alto y anchos de sobra (el mas angosto, 62.4 en Chrome con el demo; antes 65.4). */
export const AIRE_DE_LA_LEYENDA = { relleno: 3, hueco: 4, entreBotones: 2 };

function Leyenda({ t, testid, abierta, alTocar, unaLinea = false }) {
  return (
    // t7-3 remate 3 (D2): en la barra de la computadora, la leyenda va en UNA linea (nunca parte)
    <span data-leyenda="si" className="flex flex-wrap items-center relative" data-testid={testid}
      style={{ columnGap: AIRE_DE_LA_LEYENDA.entreBotones, fontSize: 12, color: GRIS,
        ...(unaLinea ? { flexWrap: 'nowrap', whiteSpace: 'nowrap' } : {}) }}>
      {ESTADOS_DE_LA_CARD.map((e) => {
        const rotulo = t(`groups.avance.estado.${e}`);
        return (
          <button key={e} type="button" data-testid={`${testid}-${e}`} aria-label={rotulo} aria-expanded={abierta === e}
            title={t(`groups.avance.leyenda_accion.${e}`)} onClick={() => alTocar(abierta === e ? null : e)}
            className="inline-flex items-center"
            style={{ gap: AIRE_DE_LA_LEYENDA.hueco, minHeight: 32, paddingLeft: AIRE_DE_LA_LEYENDA.relleno,
              paddingRight: AIRE_DE_LA_LEYENDA.relleno, background: 'transparent', color: GRIS,
              fontSize: 12, borderRadius: 6, cursor: 'pointer', ...(unaLinea ? { position: 'relative' } : {}) }}>
            <Cuadrito estado={e} />{rotulo}
            {/* post-t7-2b (D1): en la barra, cada boton (32 de alto) toca en 44 sin cambiar el dibujo; ahi la leyenda va en
                una linea y el area no se encima con nada. Debajo del subtitulo (varios renglones pegados) se queda como estaba */}
            {unaLinea && <AreaDeToque />}
          </button>
        );
      })}
      {abierta && (
        <span role="tooltip" data-testid={`${testid}-burbuja`}
          style={{ position: 'absolute', top: '100%', left: 0, zIndex: 30, marginTop: 2, background: '#262626',
            color: '#ffffff', fontSize: 12, ...relleno(6, 10), borderRadius: 8, maxWidth: 280,
            boxShadow: css('0', '4px', '12px', 'rgba(0,0,0,0.45)') }}>
          {t(`groups.avance.leyenda_accion.${abierta}`)}
        </span>
      )}
    </span>
  );
}

function Iniciales({ texto, lado = 36, testid }) {
  return (
    <span aria-hidden="true" data-testid={testid} className="inline-flex items-center justify-center shrink-0"
      style={{ width: lado, height: lado, borderRadius: 999, background: '#222222', ...borde('#262626'),
        fontSize: lado >= 44 ? 14 : (lado >= 36 ? 12 : 10), fontWeight: 700, color: GRIS }}>
      {texto}
    </span>
  );
}

/** t7-3 remate 4 (D2): un conteo "x de y" nunca se parte entre renglones: si no cabe, baja ENTERO (se hace al
    pintar; los textos de los locales no cambian). */
const SOLO_UN_CONTEO = /^\d+ (de|of) \d+$/;

function ConNegritas({ texto }) {
  return negritasXdeY(texto).map((p, i) => (p.b
    ? <strong key={i} style={{ color: BLANCO, fontWeight: 700, ...(SOLO_UN_CONTEO.test(p.t) ? { whiteSpace: 'nowrap' } : {}) }}>{p.t}</strong>
    : <React.Fragment key={i}>{p.t}</React.Fragment>));
}

/** El numero `n` de un texto en negritas ("caduca en **4** dias"). */
function NumeroEnNegritas({ texto, n }) {
  const marca = ` ${n} `;
  const i = texto.indexOf(marca);
  if (i < 0) return texto;
  return (
    <>
      {texto.slice(0, i + 1)}
      <strong style={{ color: BLANCO, fontWeight: 700 }}>{String(n)}</strong>
      {texto.slice(i + marca.length - 1)}
    </>
  );
}

function SeccionPlegable({ titulo, chip, abierta, alternar, testid, children }) {
  return (
    <section className={CAPA_DE_SECCION} data-testid={testid}>
      <h2 className={TITULO_DE_SECCION_PLEGABLE}>
        <button type="button" onClick={alternar} aria-expanded={abierta}
          className={CABECERA_DE_SECCION} data-testid={`${testid}-plegar`}>
          <span className={GUIA_DE_SECCION} aria-hidden="true" />
          <span className="flex-1 min-w-0" style={PARTE_RENGLON} data-testid={`${testid}-titulo`}>{titulo}</span>
          <span className={CHIP_DE_CONTEO} data-testid={`${testid}-conteo`}>{chip}</span>
          <ChevronDown aria-hidden="true"
            className={`w-4 h-4 text-[#8c8c8c] shrink-0 transition-transform ${abierta ? 'rotate-180' : ''}`} />
        </button>
      </h2>
      <div hidden={!abierta} className={CUERPO_DE_SECCION} data-testid={`${testid}-cuerpo`}>
        {abierta ? children : null}
      </div>
    </section>
  );
}

function PastillaPregunta({ t, testid, flotante = false, alAbrir, alto }) {
  return (
    <Button variant="ghost" className={`${PILL_BUTTON_CLASS} shrink-0`} style={alto ? { ...PASTILLA, height: alto } : PASTILLA}
      data-testid={testid} data-flotante={flotante ? 'si' : 'no'} onClick={alAbrir} aria-haspopup="dialog">
      <MessageCircle className="w-4 h-4 mr-1.5" aria-hidden="true" />
      {t('groups.avance.preguntale')}
    </Button>
  );
}

/** El contenedor de una respuesta de la portada: capa, guia de color, titulo y el "?". */
function Respuesta({ testid, guia, titulo, ayuda, children }) {
  const [verAyuda, setVerAyuda] = useState(false);
  return (
    <section style={CAPA} data-testid={testid} id={testid}>
      <div className="flex items-center" style={{ gap: 10, marginBottom: 12 }}>
        <span aria-hidden="true" data-testid={`${testid}-guia`}
          style={{ width: 3, height: 20, borderRadius: 2, background: guia, flexShrink: 0 }} />
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: BLANCO }} data-testid={`${testid}-titulo`}>{titulo}</h2>
        <button type="button" title={ayuda} aria-label={ayuda} data-testid={`${testid}-ayuda`}
          onClick={() => setVerAyuda((v) => !v)} aria-expanded={verAyuda}
          className="inline-flex items-center justify-center shrink-0"
          style={{ marginLeft: 'auto', width: 22, height: 22, borderRadius: 999, borderWidth: 0, color: '#ffffff',
            fontSize: 13, fontWeight: 700, cursor: 'help', background: NARANJA, position: 'relative' }}>
          <AreaDeToque />
          ?
        </button>
      </div>
      {verAyuda && (
        <p data-testid={`${testid}-ayuda-texto`} style={{ fontSize: 12, color: GRIS, margin: css('-4px', 0, '10px') }}>{ayuda}</p>
      )}
      {children}
    </section>
  );
}

/** t7-3 remate 7 (regla 2 de #581): el area de toque de 44 px de un control chico, sin cambiar lo que se ve: un span
    vacio, absoluto y centrado sobre el control (que va con posicion relativa), de al menos 44 por 44. */
const AREA_DE_TOQUE = { position: 'absolute', left: '50%', top: '50%', width: css('max(100%,', '44px)'), height: css('max(100%,', '44px)'),
  transform: 'translate(-50%, -50%)' };

function AreaDeToque() {
  return <span aria-hidden="true" data-area-de-toque="si" style={AREA_DE_TOQUE} />;
}

function PieNaranja({ testid, onClick, children, arriba = 12 }) {
  return (
    <button type="button" data-testid={testid} onClick={onClick} className="hover:underline"
      style={{ display: 'block', marginTop: arriba, color: NARANJA, fontSize: 13, fontWeight: 600, background: 'transparent',
        textAlign: 'left', position: 'relative' }}>
      <AreaDeToque />
      {children}
    </button>
  );
}

function Vacio({ testid, children }) {
  return <p data-testid={testid} style={{ fontSize: 13, color: GRIS }}>{children}</p>;
}

function ChipDeMotivo({ motivo, testid, children }) {
  return (
    <span data-testid={testid}
      style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, borderRadius: 999, ...relleno(2, 9),
        background: COLOR_DEL_CHIP[motivo], color: TEXTO_DEL_CHIP[motivo],
        ...borde(BORDE_DEL_CHIP[motivo], ANCHO_DEL_BORDE_DEL_CHIP[motivo] || 1),
        verticalAlign: 'middle', lineHeight: 1.35 }}>
      {children}
    </span>
  );
}

/** t7-3 remate 8 (#591, C50): el ancho del boton "Recordar" con su texto mas largo ("Recordado hoy" / "Reminded
    today", 12 px en negritas, con su relleno): asi la barra del alumno no cambia de largo al recordar. */
export const ANCHO_MINIMO_DE_RECORDAR = 116;

function BotonRecordar({ t, alumno, recordados, enviando, recordar, testid, anchoMinimo = true }) {
  const yaSe = !!(recordados[alumno.id] || alumno.recordado_hoy);
  return (
    <button type="button" onClick={soloEste(() => recordar(alumno))} data-testid={testid}
      aria-disabled={yaSe || enviando[alumno.id] ? 'true' : undefined} data-enviado={yaSe ? 'si' : 'no'}
      // t7-3 remate 8 (C90): mientras va en camino se apaga (aria-disabled) y no admite otro toque
      aria-busy={enviando[alumno.id] ? 'true' : undefined}
      className="shrink-0 whitespace-nowrap rounded-full relative"
      style={{ height: 32, paddingLeft: 12, paddingRight: 12, fontSize: 12, fontWeight: 600, ...borde('#333333'),
        color: yaSe ? GRIS : BLANCO, background: 'transparent', cursor: yaSe || enviando[alumno.id] ? 'default' : 'pointer',
        // t7-3 remate 8 (#591, C50): un ancho minimo para que la barra del alumno no cambie de largo con "Recordado hoy"
        // t7-3 remate 8 (instrumentos, A2): en el renglon de riesgo con nombres (3 alumnos o menos) va a su ancho natural:
        // ahi el minimo le dejaba al nombre 57 px y partia los nombres largos a media palabra
        minWidth: anchoMinimo ? ANCHO_MINIMO_DE_RECORDAR : undefined, opacity: enviando[alumno.id] ? 0.6 : 1 }}>
      {/* el area tocable de 44 en tactil: seis pixeles arriba y abajo, sin mover nada */}
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: -7, bottom: -7 }} />
      {yaSe ? t('groups.avance.recordado_hoy') : t('groups.avance.recordar')}
    </button>
  );
}

/* ======================================================================
   LA PAGINA
   ====================================================================== */

// post-t7-2c (F1, Anexo D.3): `soloEsqueleto` lo pone `InstitutionalRoute` mientras la sesion de la web resuelve: la
// pagina pinta su esqueleto de carga sin pedir el avance (tampoco al volver a la pestana); al quitarse, esta misma
// instancia lo pide.
export default function GroupAvancePage({ soloEsqueleto = false } = {}) {
  const { t, i18n } = useTranslation(['dashboard', 'common', 'study']);
  const { groupId } = useParams();
  const navigate = useNavigate();
  const lang = idiomaDe(i18n);
  // post-t7-2b (remate de D1 y D7): el boton "Ver avance" de la pagina del grupo manda en el estado de la ruta el grupo de
  // su resumen (nombre y conteos): el esqueleto arma con el la barra y el subtitulo reales. Solo si es ESTE grupo
  const ubicacion = usarUbicacion();
  const delRecuadro = ubicacion && ubicacion.state && ubicacion.state.grupoDelAvance;
  const grupoDelRecuadro = delRecuadro && typeof delRecuadro === 'object' && delRecuadro.id === groupId ? delRecuadro : null;
  const tt = useCallback((llave, p = {}) => t(llave, conAlias(p)), [t]);

  const [datos, setDatos] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [errorDeCarga, setErrorDeCarga] = useState(false);
  // Al entrar SIEMPRE: la portada abierta y las tres secciones plegadas; no se recuerda.
  const [abiertas, setAbiertas] = useState({ cards: false, alumnos: false, tabla: false });
  const [cardAbierta, setCardAbierta] = useState(null);
  const [alumnoAbierto, setAlumnoAbierto] = useState(null);
  const [pestana, setPestana] = useState('resumen');
  const [recordados, setRecordados] = useState({});
  const [enviando, setEnviando] = useState({});
  const [enviandoTodos, setEnviandoTodos] = useState({});
  const [globo, setGlobo] = useState(null);
  const [chatAbierto, setChatAbierto] = useState(false);
  const [leyenda, setLeyenda] = useState(null);
  const [plegados, setPlegados] = useState({});
  const [busqueda, setBusqueda] = useState('');
  const [temaElegido, setTemaElegido] = useState(null);
  const [explicar, setExplicar] = useState(false);
  const [mejoraronTodos, setMejoraronTodos] = useState(false);
  const [motivoAbierto, setMotivoAbierto] = useState(null);
  const pastillaRef = useRef(null);
  const raizRef = useRef(null);
  const ancho = useAnchoDeCaja(raizRef);
  const angosto = esAngosto(ancho, UMBRAL_ANGOSTO);
  const apilada = esAngosto(ancho, UMBRAL_TRES_COLUMNAS);
  // t7-3 remate 4 (C1): la barra de dos renglones con la leyenda a la derecha del boton, SOLO cuando cabe
  // (W >= L + 20 + 300, medidos antes de pintar: `lib/disposicionDeLaBarra.js`); si no, la del telefono, sea cual
  // sea la ventana. Sin medida, la de siempre. LAPIDA: murio el corte fijo de 1024 de ventana (remate 3, D2), que
  // no contaba el menu lateral.
  // post-t7-2b (D1): W >= L + 3 x 20 + la reserva del boton (su ancho real B, del medidor de la pagina, si es menor que
  // 220; si no, o en el esqueleto, 220). LAPIDA: murio W >= L + 20 + 300 (contaba uno de los tres huecos del grid).
  // Remate de post-t7-2c (R4): la reserva es B ENTERO cuando se midio (la computadora solo si el nombre cabe en un
  // renglon); 220 solo sin medida. LAPIDA: decia "si es menor que 220; si no, 220".
  const medidas = useMedidasDeLaBarra(raizRef, ancho,
    `${lang}|${datos ? 'pagina' : 'esqueleto'}|${(datos && datos.grupo && datos.grupo.nombre) || ''}`);
  // Remate de post-t7-2c (R4.2): el esqueleto sin medidor del boton (no conoce el nombre) decide con el B que la pagina
  // recordo de este grupo (`botonDelRecuerdo`); sin recuerdo, el minimo. La pagina siempre decide con su B medido.
  const botonParaLaRegla = medidas.B > 0 ? medidas.B : (datos ? 0 : botonDelRecuerdo(recuerdoDelGrupo(groupId)));
  const enBarra = !angosto && cabeLaBarraDeComputadora(medidas.W, medidas.L, botonParaLaRegla) !== false;
  // C4: sin la barra de navegacion de abajo (768 o mas de ventana), la flotante se apoya a 12 px del borde
  const ventanaAncha = useMedia(CONSULTA_ANCHO, true);
  const pendienteRef = useRef(null);
  const enfocarPrincipalRef = useRef(false);
  // post-t7-1 (A7): en computadora, despues de elegir otro tema en rojo, mirar si el titulo del principal se ve
  const revisarPrincipalRef = useRef(false);
  const enfocarAnclaRef = useRef(false);
  const barraRef = useRef(null);

  const datosRef = useRef(null);
  const load = useCallback(async () => {
    // t7-3 remate 8 (#591, C29 y C129): con datos a la vista, una recarga que falla los deja y avisa chico
    if (!datosRef.current) setErrorDeCarga(false);
    try {
      // t7-3 remate 8 (#589): `motivos=5` pide el quinto motivo (sin la marca, el servidor manda los cuatro de siempre)
      const res = await api.get(`/grupos/${groupId}/avance`, { params: { lang, tz: zonaDelNavegador(), motivos: MARCA_DE_MOTIVOS } });
      // t7-3 remate 7 (regla 7 de #581): con datos nuevos manda el servidor (su `recordado_hoy`); lo marcado en esta
      // pagina se olvida, o "Recordado hoy" se quedaba de un dia para otro con la pagina abierta
      setRecordados({});
      datosRef.current = res.data;
      setErrorDeCarga(false);
      setDatos(res.data);
      // pasada 3 (regla 7): un 403 o 404 pasajero no se queda pegado; con datos buenos, la pagina vuelve
      setNotFound(false);
    } catch (err) {
      if (err.response?.status === 404 || err.response?.status === 403) { setNotFound(true); return; }
      if (datosRef.current) {
        // la tarjeta de error completa solo cuando no hay nada que mostrar; aqui, un aviso chico con texto que ya existe
        toast.error(t('groups.avance.error_de_carga'), { id: 'avance-recarga' });
        return;
      }
      setErrorDeCarga(err.response ? 'servidor' : 'red');
    }
  }, [groupId, lang, t]);

  useEffect(() => { if (!soloEsqueleto) load(); }, [load, soloEsqueleto]);

  // post-t7-1 (E2): la zona del navegador se guarda con su peticion protegida, no con la consulta. Si el servidor
  // trae la zona guardada (uno viejo no la trae) y es otra, se manda UNA vez por pagina y se vuelve a pedir el avance;
  // si falla, la pagina sigue con lo que tiene (la consulta ya calculo con la zona del navegador).
  const zonaMandadaRef = useRef(false);
  useEffect(() => {
    const grupo = datos?.grupo;
    if (!grupo || !Object.prototype.hasOwnProperty.call(grupo, 'zona_guardada') || zonaMandadaRef.current) return;
    const zona = zonaDelNavegador();
    if (!zona || zona === grupo.zona_guardada) return;
    zonaMandadaRef.current = true;
    api.post('/grupos/zona', { tz: zona }).then(() => load()).catch(() => {});
  }, [datos, load]);

  // t7-3 remate 7 (pasada 2, regla 7 de #581): el panel aterriza ARRIBA. El chasis (`<main>` de AppLayout) es el mismo
  // nodo en todas las pantallas protegidas y su desplazamiento sobrevive al cambio de ruta (el patron de SettingsPage y
  // Analytics): sin esto el panel abria a media pagina y los titulos de las tres respuestas quedaban fuera de la vista.
  useEffect(() => {
    const chasis = document.querySelector('[data-testid="content-area"]');
    if (chasis) chasis.scrollTop = 0;
  }, []);

  // Al volver a la pestana, UNA llamada mas (nada de sondeo).
  useEffect(() => {
    if (soloEsqueleto) return undefined;
    const alVolver = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => document.removeEventListener('visibilitychange', alVolver);
  }, [load, soloEsqueleto]);

  // t7-3 remate 8 (#583, C67): los numeros de "los ultimos 7 dias" cambian a la medianoche del profe. UN temporizador a
  // la siguiente medianoche del navegador (mas 5 s) pide el avance una vez; nada de sondeo. Con la pestana oculta a esa
  // hora no hace nada: basta la recarga que ya existe al volver a ella.
  useEffect(() => {
    if (datos === null) return undefined;
    const temporizador = setTimeout(() => {
      if (document.visibilityState !== 'hidden') load();
    }, msHastaLaMedianoche());
    return () => clearTimeout(temporizador);
  }, [load, datos]);

  // t7-3 remate 8 (revisor de flujos, F2): despues de elegir otro tema en rojo, el foco a la tarjeta del principal
  useEffect(() => {
    if (!enfocarPrincipalRef.current) return;
    enfocarPrincipalRef.current = false;
    const el = document.querySelector('[data-testid="avance-tema-principal"]');
    if (el && typeof el.focus === 'function') el.focus({ preventScroll: true });
  });

  // Bajar a un ancla DESPUES de abrir la seccion (un cuadro despues del render). t7-3 remate 2 (R6, #566):
  // el ancla queda pegada BAJO la barra fija, con su aire: `scroll-margin-top` = el alto de la barra + 8.
  useEffect(() => {
    const id = pendienteRef.current;
    if (!id) return;
    pendienteRef.current = null;
    const el = document.getElementById(id);
    if (!el) return;
    const barra = barraRef.current ? barraRef.current.offsetHeight : 0;
    el.style.scrollMarginTop = `${Math.max(barra, ALTO_DE_LA_BARRA) + AIRE_BAJO_LA_BARRA}px`;
    if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    // t7-3 remate 8 (revisor de flujos, segunda pasada, F4): el foco baja con la vista, al primer control del ancla
    // (antes se quedaba en la portada y el siguiente Tab regresaba la vista arriba)
    if (enfocarAnclaRef.current) {
      enfocarAnclaRef.current = false;
      const control = el.querySelector('button,[tabindex="0"],a[href],input');
      if (control && typeof control.focus === 'function') control.focus({ preventScroll: true });
    }
  });

  // post-t7-1 (#596): el letrero de un cuadrito dura 3 s (antes 2) y tambien se quita al tocar o dar clic fuera de su
  // cuadrito y con Esc (el mismo patron de la burbuja de la leyenda, con su limpieza al desmontar). Tocar otro cuadrito
  // abre el suyo, como antes.
  // post-t7-1 (A7): en computadora la vista sube SOLO si el titulo del tema principal quedo tapado por la barra fija o
  // fuera de la ventana; si ya se ve, no se mueve nada (en el telefono ya sube siempre, por `pendienteRef`).
  useEffect(() => {
    if (!revisarPrincipalRef.current) return;
    revisarPrincipalRef.current = false;
    const titulo = document.querySelector('[data-testid="avance-tema-principal-nombre"]');
    const columna = document.getElementById('avance-col-temas');
    if (!titulo || !columna || typeof titulo.getBoundingClientRect !== 'function') return;
    const r = titulo.getBoundingClientRect();
    const barra = barraRef.current ? barraRef.current.getBoundingClientRect().bottom : 0;
    const alto = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!tituloTapado(r, barra, alto)) return;
    const altoBarra = barraRef.current ? barraRef.current.offsetHeight : 0;
    columna.style.scrollMarginTop = `${Math.max(altoBarra, ALTO_DE_LA_BARRA) + AIRE_BAJO_LA_BARRA}px`;
    if (typeof columna.scrollIntoView === 'function') columna.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  useEffect(() => {
    if (!globo) return undefined;
    const temporizador = setTimeout(() => setGlobo(null), DURACION_DEL_LETRERO_MS);
    const fuera = (e) => {
      const dentro = e.target && typeof e.target.closest === 'function' && e.target.closest('[data-celda-del-letrero]');
      if (!dentro) setGlobo(null);
    };
    const esc = (e) => { if (e.key === 'Escape') setGlobo(null); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera);
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      clearTimeout(temporizador);
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('touchstart', fuera);
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [globo]);

  // #549: la burbuja de la leyenda se cierra al tocar fuera de la leyenda y con Esc.
  useEffect(() => {
    if (!leyenda) return undefined;
    const fuera = (e) => {
      const dentro = e.target && typeof e.target.closest === 'function' && e.target.closest('[data-leyenda]');
      if (!dentro) setLeyenda(null);
    };
    const esc = (e) => { if (e.key === 'Escape') setLeyenda(null); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('touchstart', fuera);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('touchstart', fuera);
      document.removeEventListener('keydown', esc);
    };
  }, [leyenda]);

  const alternar = (sec) => setAbiertas((a) => ({ ...a, [sec]: !a[sec] }));

  /** El motivo de un alumno en "Por alumno" (o "sin"). */
  const motivoDe = (uid) => {
    const m = (datos?.riesgo_por_motivo || []).find((x) => (x.alumnos || []).includes(uid));
    return m ? m.motivo : 'sin';
  };
  const abrirAlumno = (uid) => {
    setAbiertas((a) => ({ ...a, alumnos: true }));
    setPlegados((p) => ({ ...p, [motivoDe(uid)]: false }));
    setBusqueda('');
    setAlumnoAbierto(uid);
    pendienteRef.current = `avance-alumno-${uid}`;
    enfocarAnclaRef.current = true;
  };
  const verAlumnos = () => {
    setAbiertas((a) => ({ ...a, alumnos: true }));
    setPlegados({});
    pendienteRef.current = 'avance-seccion-alumnos';
    enfocarAnclaRef.current = true;
  };
  // LAPIDA t7-2d remate (#552): murio `verMotivo` (tocar el renglon de riesgo abria "Por alumno" en esa
  // seccion); hoy el renglon se despliega en su sitio con la lista del motivo.
  const alternarMotivo = (motivo) => setMotivoAbierto((x) => (x === motivo ? null : motivo));
  // t7-3 remate 2 (R6, #566): "Ver todos los temas" abre "Por publicacion" con la PRIMERA publicacion abierta
  // (su "Tema por tema" a la vista) y baja hasta el titulo de la seccion.
  const verPorCard = () => {
    setAbiertas((a) => ({ ...a, cards: true }));
    const primera = (datos?.cards || [])[0];
    if (primera) setCardAbierta(primera.publicacion_id);
    pendienteRef.current = 'avance-seccion-cards';
    enfocarAnclaRef.current = true;
  };
  const abrirCard = (cid) => {
    setAbiertas((a) => ({ ...a, cards: true }));
    setCardAbierta(cid);
    pendienteRef.current = `avance-mosaico-${cid}`;
    enfocarAnclaRef.current = true;
  };

  /** t7-3 remate 7 (regla 7 de #581): el aviso de un error de "Recordar" segun lo que paso, con avisos que ya existen:
      sin respuesta, "Error de red"; 429, el freno; el alumno ya no es del grupo, su aviso; cualquier otro, el general
      (antes todo error decia "Error de red. Verifica tu conexion.", tambien un 404 o un 500 con la red bien). */
  const avisoDelError = (err) => {
    const r = err && err.response;
    if (!r) return t('common:errors.network');
    if (r.status === 429) return t('common:errors.rate_limit_generic');
    if (r.data && r.data.error_code === 'group_member_not_found') return t('common:errors.group_member_not_found');
    return t('common:toasts.generic_error');
  };

  const recordar = async (alumno) => {
    if (recordados[alumno.id] || alumno.recordado_hoy) {
      toast.info(t('groups.avance.ya_recordado'), { id: `recordar-${alumno.id}` });
      return;
    }
    if (enviando[alumno.id]) return;
    setEnviando((e) => ({ ...e, [alumno.id]: true }));
    try {
      const { data } = await api.post(`/grupos/${groupId}/recordar/${alumno.id}`);
      if (data && (data.enviado || data.ya_enviado_hoy)) {
        setRecordados((r) => ({ ...r, [alumno.id]: true }));
        // otro toque (otra pestana) ya lo habia mandado hoy: no salio nada nuevo
        if (!data.enviado) toast.info(t('groups.avance.ya_recordado'), { id: `recordar-${alumno.id}` });
      } else {
        // no salio ni la notificacion ni el correo: la pastilla sigue en "Recordar"
        toast.error(t('common:toasts.generic_error'), { id: `recordar-${alumno.id}` });
      }
    } catch (err) {
      // pasada 4 (regla 7): con su id, como sus hermanos: otro toque no apila otro aviso igual
      toast.error(avisoDelError(err), { id: `recordar-${alumno.id}` });
      // t7-3 remate 8 (#591): si ya no esta en el grupo, el panel se recarga solo y el alumno desaparece
      if (err && err.response && err.response.data && err.response.data.error_code === 'group_member_not_found') load();
    } finally {
      setEnviando((e) => ({ ...e, [alumno.id]: false }));
    }
  };

  /** #547: "Recordar a todos ellos": UNA peticion con la lista; el servidor aplica la regla de
      "Recordar" alumno por alumno y contesta el resultado de cada uno. */
  const recordarTodos = async (clave, ids) => {
    if (!ids.length || enviandoTodos[clave]) return;
    setEnviandoTodos((e) => ({ ...e, [clave]: true }));
    try {
      const { data } = await api.post(`/grupos/${groupId}/recordar-varios`, { alumno_ids: ids });
      const resultados = (data && data.resultados) || [];
      const hechos = resultados.filter((r) => r.resultado === 'enviado' || r.resultado === 'ya_avisado_hoy');
      setRecordados((r) => ({ ...r, ...Object.fromEntries(hechos.map((x) => [x.alumno_id, true])) }));
      // pasada 3 (regla 7): el alumno que ya no es del grupo avisa como en "Recordar" de uno; si no salio ninguno, el
      // aviso de siempre (antes, con la lista vacia o solo "no es del grupo", no pasaba nada)
      const fueraN = resultados.filter((r) => r.resultado === 'no_es_del_grupo').length;
      const fuera = fueraN > 0;
      // pasada 4 (regla 5): con dos o mas, en plural (con uno, la letra del "Recordar" de uno)
      if (fuera) {
        toast.error(t('groups.avance.fuera_del_grupo', { count: fueraN }),
          { id: `recordar-todos-fuera-${clave}` });
        // t7-3 remate 8 (#591): el panel se recarga solo y quien ya no esta desaparece
        load();
      }
      if (resultados.some((r) => r.resultado === 'no_se_pudo') || (!hechos.length && !fuera)) {
        toast.error(t('groups.avance.no_se_pudo_mandar'), { id: `recordar-todos-${clave}` });
      }
    } catch (err) {
      // t7-3 remate 8 (#591, C17 y C133): el error se dice como en "Recordar" (el freno, la red, el general)
      toast.error(avisoDelError(err), { id: `recordar-todos-${clave}` });
    } finally {
      setEnviandoTodos((e) => ({ ...e, [clave]: false }));
    }
  };

  const volver = () => navigate(`/groups/${groupId}`);

  // t7-2b (#518): cualquiera de las pastillas abre el cajon; al cerrar, el foco vuelve a esa.
  const abrirChat = (e) => {
    pastillaRef.current = e && e.currentTarget ? e.currentTarget : null;
    setChatAbierto(true);
  };
  const cerrarChat = useCallback(() => setChatAbierto(false), []);
  // pasada 3 (regla 7): con "Este grupo no existe, o no es tuyo." a la vista no hay boton del chat
  const chatDisponible = !notFound && datos?.grupo?.chat_disponible === true;
  useEffect(() => { if (!chatDisponible) setChatAbierto(false); }, [chatDisponible]);
  // post-t7-2b (D7): si al cargar el boton flotante del chat tapa un control ("Ver quienes" a 320), espera oculto hasta
  // que se desliza la pagina (`lib/telefonoDelAvance.js`)
  const flotanteRef = useRef(null);
  // post-t7-2c (D2): en cualquier disposicion (la flotante es el unico boton del chat). LAPIDA: decia
  // `chatDisponible && !enBarra && !!datos`.
  const flotanteEspera = useFlotanteQueEspera(chatDisponible && !!datos, datos, flotanteRef, raizRef,
    '[data-testid="avance-page"]');
  // post-t7-2b (remate de D1 y D7): con los datos pintados se recuerda lo que el esqueleto de la proxima carga necesita
  // (`lib/esqueletoDelAvance.js`): si hay chat, el subtitulo de este grupo y, en computadora, el alto real de su barra
  useLayoutEffect(() => {
    if (!datos || !datos.grupo || notFound) return;
    recordarChat(chatDisponible);
    // Remate de post-t7-2c (R4.2 y la decision del 15-oct): la barra recordada lleva el B medido del boton y el alto
    // real de la barra en las DOS disposiciones (`barraRecordada`: la de computadora donde estaba, la del telefono en
    // `telefono`). LAPIDA: solo se media en computadora (`enBarra && barraRef.current`) y en el telefono no se recordaba.
    const alto = barraRef.current ? (barraRef.current.getBoundingClientRect() || {}).height : 0;
    const previa = (recuerdoDelGrupo(groupId) || {}).barra || null;
    recordarDelGrupo(groupId, {
      lang, subtitulo: subtituloDe(t, datos.grupo),
      barra: barraRecordada(previa, { enBarra, lang, W: medidas.W, alto, B: medidas.B }),
    });
  // `medidas.vuelta` cambia con el ResizeObserver de la raiz y al cargar las fuentes: la barra puede crecer despues (el
  // nombre parte renglon con la letra definitiva)
  }, [datos, notFound, chatDisponible, enBarra, groupId, lang, t, medidas.W, medidas.B, medidas.vuelta]);
  // t7-3 remate 8 (revisor de flujos, segunda pasada, F3): con "no existe" o el error de carga a la vista, si el foco
  // se quedo sin dueno (en el fondo de la pagina), pasa a la pildora de regreso
  useEffect(() => {
    if (!notFound && !errorDeCarga) return;
    const activo = document.activeElement;
    if (activo && activo !== document.body && activo.isConnected) return;
    const caja = document.querySelector('[data-testid="avance-notfound-volver"]')
      || document.querySelector('[data-testid="avance-error-volver"]');
    const pildora = caja ? caja.querySelector('button') : null;
    if (pildora) pildora.focus();
  }, [notFound, errorDeCarga]);

  const alumnoPorId = Object.fromEntries((datos?.alumnos || []).map((a) => [a.id, a]));
  const comun = { t, tt, lang, datos, recordados, enviando, recordar };
  let contenido;
  if (notFound) {
    // t7-3 remate 8 (#591, C36 y C130): la pildora de regreso de la casa, con sus textos que ya existen
    contenido = (
      <>
        <StickyBackPill onClick={() => navigate('/groups')} label={t('groups.back_btn')} testid="avance-notfound-volver" />
        <div className="glass-card rounded-[20px] p-8 text-center" data-testid="avance-notfound">
          <p className="text-sm text-[#a0a0a0]">{t('groups.not_found')}</p>
        </div>
      </>
    );
  } else if (errorDeCarga) {
    // t7-3 remate 8 (#591, C13 y C89): su aviso propio; "se cayo la conexion" solo sin respuesta del servidor
    contenido = (
      <>
        <StickyBackPill onClick={volver} label={t('groups.bank.temas.back')} testid="avance-error-volver" />
        <GroupsLoadError onRetry={load} testid="avance-error" titulo={t('groups.avance.error_de_carga')}
          sinRed={errorDeCarga === 'red'} />
      </>
    );
  } else if (datos === null) {
    const recuerdo = recuerdoDelGrupo(groupId);
    const anchoDelBoton = medidas.W > 0 && medidas.L > 0
      ? Math.round((medidas.W - medidas.L - HUECOS_DE_LA_BARRA * HUECO_DE_LA_BARRA) * 10) / 10 : null;
    contenido = (<EsqueletoDelAvance apilada={apilada} angosto={angosto} enBarra={enBarra} t={t}
      subtitulo={grupoDelRecuadro ? subtituloDe(t, grupoDelRecuadro) : (recuerdo && recuerdo.sub ? recuerdo.sub[lang] : undefined)}
      altoDeLaBarra={altoDeLaBarraDelEsqueleto(groupId, medidas.W, lang)}
      altoDelTelefono={altoDelTelefonoDelRecuerdo(recuerdo, medidas.W, lang)}
      nombre={grupoDelRecuadro && typeof grupoDelRecuadro.nombre === 'string' ? grupoDelRecuadro.nombre : ''}
      anchoDelBoton={anchoDelBoton} />);
  } else {
    contenido = pintar();
  }
  return (
    // t7-3 remate 7 (regla 2 de #581): con la flotante del chat a la vista queda aire al final (la flotante mide 44 y va
    // 12 px arriba de su borde): sin el, tapaba el ultimo renglon de la pagina
    <div ref={raizRef} data-testid="avance-raiz" data-angosto={angosto ? 'si' : 'no'}
      style={chatDisponible && datos ? { paddingBottom: AIRE_BAJO_LA_FLOTANTE } : undefined}>
      {contenido}
      {/* t7-3 remate 4 (C4): con el chat encendido, EXACTAMENTE un boton a la vista. Abajo a la derecha, arriba de la
          barra de navegacion donde la hay; donde no (768 o mas), a la misma distancia del borde (12 px). Va FUERA del
          contenedor animado: un ancestro con transform la anclaria a la pagina.
          post-t7-2c (D2, #o): ese boton es SIEMPRE esta flotante, tambien en la disposicion de computadora (y el aire
          al final de la pagina y la espera oculta, en todos los anchos). LAPIDA: en la de computadora el boton iba en
          el renglon 2 de la barra (`avance-pastilla-barra`) y esta flotante solo se pintaba en la del telefono. */}
      {chatDisponible && !!datos && (
        <div className={`fixed right-4 z-40 mb-3 ${ACTION_BAR_BOTTOM_CLASS}`} data-testid="avance-pastilla-flotante"
          ref={flotanteRef} data-espera={flotanteEspera ? 'si' : 'no'} aria-hidden={flotanteEspera ? 'true' : undefined}
          style={ventanaAncha || flotanteEspera
            ? { ...(ventanaAncha ? FLOTANTE_SIN_BARRA_DE_ABAJO : {}), ...(flotanteEspera ? { visibility: 'hidden' } : {}) }
            : undefined}>
          <PastillaPregunta t={t} testid="avance-pastilla-telefono" flotante alAbrir={abrirChat} />
        </div>
      )}
      {chatDisponible && (
        <PanelChatCajon abierto={chatAbierto} alCerrar={cerrarChat} groupId={groupId}
          grupoNombre={datos.grupo.nombre} hora={horaLocal(datos.grupo.calculado_en, lang)}
          avance={datos} volverA={pastillaRef} />
      )}
    </div>
  );

  function pintar() {
    const g = datos.grupo;
    const hora = horaLocal(g.calculado_en, lang);
    // post-t7-2b (D7): el subtitulo sale de `subtituloDe`, que tambien pinta (oculto) el esqueleto
    const subtitulo = subtituloDe(t, g);
    const sinCards = g.cards_publicadas === 0;
    const sinAlumnos = !sinCards && g.alumnos === 0;
    const textoBarra = tt('groups.avance.barra_titulo', { hora });
    // t7-3 remate 8 (#585): el boton de regresar, segun la disposicion
    const boton = enBarra ? BOTON_VOLVER.computadora : BOTON_VOLVER.telefono;
    // t7-3 (#556, regla 22): "Avance del grupo. Datos de hoy, {{hora}}"; lo de antes del primer punto va en negritas
    const corte = textoBarra.indexOf('. ');
    const antes = corte >= 0 ? textoBarra.slice(0, corte) : textoBarra;
    const resto = corte >= 0 ? textoBarra.slice(corte + 2) : '';
    const textoDeLaBarra = (extra) => (
      <span data-testid="avance-barra-texto" style={{ fontSize: 13, color: GRIS, ...PARTE_RENGLON, ...extra }}>
        <strong style={{ color: BLANCO, fontWeight: 600 }}>{antes}</strong>{resto ? `. ${resto}` : ''}
      </span>
    );

    return (
      <div className="animate-fade-in-up" data-testid="avance-page">
        {/* LA BARRA FIJA (seccion 3.1): en el flujo arriba y pegada al bajar. t7-3 remate 3 (D1 y D2): dos
            renglones y nada cortado: el boton de regresar con el nombre del grupo ENTERO (parte renglon dentro) y,
            debajo, "Avance del grupo. Datos de hoy, {{hora}}" entero. Desde 1024 de ventana, la leyenda va en el
            renglon 1, en una linea, centrada en el espacio libre a la derecha del boton (la columna de la leyenda
            nunca se encoge: cede la del boton), y el boton del chat en el renglon 2, alineado al final de la
            leyenda. Debajo de 1024, como en el telefono. */}
        <div className={`sticky ${PEGAJOSA_A_LO_ANCHO_CLASS} z-20`}
          data-testid="avance-barra" ref={barraRef} data-disposicion={enBarra ? 'computadora' : 'telefono'}
          style={{ background: 'rgba(14,14,14,0.94)', borderBottomWidth: 1, borderBottomStyle: 'solid',
            borderBottomColor: '#1f1f1f', minHeight: 56, alignItems: 'center',
            paddingTop: 8, paddingBottom: 8, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            ...(enBarra
              // post-t7-2b (D1): el hueco sale de la regla (`HUECO_DE_LA_BARRA`, 20): cuatro columnas, tres huecos
              ? { display: 'grid', gridTemplateColumns: css('minmax(0,', 'max-content)', '1fr', 'max-content', '1fr'),
                columnGap: HUECO_DE_LA_BARRA, rowGap: 6 }
              : { display: 'flex', flexWrap: 'wrap', columnGap: 16, rowGap: 6 }) }}>
          {/* post-t7-2c (D3, #n): el nombre del grupo en UN renglon (puntos suspensivos si no cabe), el nombre entero
              en `title` y "Volver a {{nombre}}" para el lector de pantalla. LAPIDA: el nombre partia renglon dentro
              del boton (`PARTE_RENGLON`). */}
          <button type="button" onClick={volver} data-testid="avance-volver"
            className="inline-flex items-center" title={g.nombre}
            aria-label={t('groups.avance.volver_a', { nombre: g.nombre })}
            style={{ ...estiloDelVolver(boton),
              maxWidth: '100%', textAlign: 'left', lineHeight: 1.3, justifySelf: 'start', position: 'relative',
              ...(enBarra ? { gridColumn: '1', gridRow: '1' } : {}) }}>
            <AreaDeToque />
            <span aria-hidden="true" style={{ flexShrink: 0 }}>{'\u2190'}</span>
            {/* Remate de post-t7-2c (15-oct): en el telefono, hasta dos renglones. LAPIDA: un renglon tambien ahi (D3). */}
            <span style={enBarra ? UN_RENGLON : NOMBRE_EN_DOS_RENGLONES} data-renglones={enBarra ? '1' : '2'}>
              {g.nombre}
            </span>
          </button>
          <MedidorDelVolver nombre={g.nombre} />
          {enBarra && (
            <span style={{ gridColumn: '3', gridRow: '1', alignSelf: 'center' }}>
              <Leyenda t={t} testid="avance-barra-leyenda" abierta={leyenda} alTocar={setLeyenda} unaLinea />
            </span>
          )}
          {/* t7-3 remate 4 (C2): en la computadora el renglon 2 ocupa las columnas del boton, del espacio libre y
              de la leyenda: el titulo en UN renglon. LAPIDA post-t7-2c (D2, #o): con el chat encendido llevaba su
              boton al final de la leyenda (`avance-pastilla-barra`); hoy el boton del chat es siempre la flotante. */}
          {enBarra ? (
            <span data-testid="avance-barra-renglon-2"
              style={{ gridColumn: css('1', '/', '4'), gridRow: '2', display: 'flex', alignItems: 'center', columnGap: 16,
                minWidth: 0 }}>
              {textoDeLaBarra({ flexGrow: 1, flexShrink: 1 })}
            </span>
          ) : textoDeLaBarra({ flexBasis: '100%', order: 2, paddingLeft: SANGRIA_DEL_RENGLON_TELEFONO })}
        </div>

        {/* t7-2d remate (#550): por debajo de 768 px (el umbral del menu lateral) el titulo grande no se
            pinta: la barra fija ya dice "Avance del grupo". Desde 768, igual que antes. */}
        <div className="md:hidden" aria-hidden="true" style={{ height: 14 }} />
        <h1 className="hidden md:block" style={{ fontSize: 28, fontWeight: 800, color: '#fff', lineHeight: 1.2, margin: css('22px', 0, '2px') }}
          data-testid="avance-titulo">
          {t('groups.avance.titulo')}
        </h1>
        <p style={{ fontSize: 14, color: GRIS, marginBottom: angosto ? 8 : 18 }} data-testid="avance-subtitulo">{subtitulo}</p>
        {!enBarra && (
          <div style={{ marginBottom: 18 }}>
            <Leyenda t={t} testid="avance-leyenda" abierta={leyenda} alTocar={setLeyenda} />
          </div>
        )}

        {sinCards ? (
          <div style={{ ...TARJETA, ...relleno(24, 20) }} className="text-center space-y-4" data-testid="avance-sin-cards">
            <p style={{ fontSize: 14, color: GRIS }}>{t('groups.avance.sin_cards_largo')}</p>
            <Button onClick={volver} className="bg-[#FF6B00] hover:bg-[#E05A00] text-white rounded-xl h-11 font-bold"
              data-testid="avance-ir-al-banco">
              {t('groups.avance.ir_al_banco')}
            </Button>
          </div>
        ) : sinAlumnos ? (
          <div style={{ ...TARJETA, ...relleno(24, 20) }} className="text-center" data-testid="avance-sin-alumnos">
            <p style={{ fontSize: 14, color: GRIS }}>{t('groups.avance.sin_alumnos')}</p>
          </div>
        ) : (
          <>
            <div data-testid="avance-portada" data-apilada={apilada ? 'si' : 'no'}
              style={apilada ? { display: 'flex', flexDirection: 'column', gap: 14 }
                : { display: 'grid', gridTemplateColumns: css('1fr', '1.15fr', '1fr'), gap: 14, alignItems: 'start' }}>
              <ColRiesgo {...comun} abrirAlumno={abrirAlumno} verAlumnos={verAlumnos} motivoAbierto={motivoAbierto}
                alternarMotivo={alternarMotivo} alumnoPorId={alumnoPorId} recordarTodos={recordarTodos}
                enviandoTodos={enviandoTodos} />
              {/* t7-3 remate 8 (#591, C30): en el telefono, tocar un "otro tema en rojo" sube la vista al tema principal */}
              {/* t7-3 remate 8 (revisor de flujos, F2): y el foco pasa a la tarjeta del tema principal (el renglon elegido
                  desaparece de la lista y el foco caia al fondo de la pagina) */}
              <ColTemas {...comun} elegido={temaElegido} elegir={(id) => {
                setTemaElegido(id); setExplicar(false); if (angosto) pendienteRef.current = 'avance-col-temas';
                else revisarPrincipalRef.current = true;
                enfocarPrincipalRef.current = true;
              }}
                explicar={explicar} setExplicar={setExplicar} verPorCard={verPorCard} angosto={angosto} />
              <ColMejora {...comun} todos={mejoraronTodos} verTodos={() => setMejoraronTodos(true)} />
            </div>

            <AvisosDeCard {...comun} abrirCard={abrirCard} apilada={apilada} />

            <div className="space-y-3" style={{ marginTop: 14 }}>
              <div id="avance-seccion-cards">
                <SeccionPlegable titulo={t('groups.avance.seccion.por_card')} abierta={abiertas.cards}
                  alternar={() => alternar('cards')} testid="avance-seccion-cards"
                  chip={t('groups.avance.publicaciones', { count: g.cards_publicadas })}>
                  <PorCard {...comun} angosto={angosto} abierta={cardAbierta}
                    alternar={(id) => setCardAbierta((x) => (x === id ? null : id))} />
                </SeccionPlegable>
              </div>
              <div id="avance-seccion-alumnos">
                <SeccionPlegable titulo={t('groups.avance.seccion.por_alumno')} abierta={abiertas.alumnos}
                  alternar={() => alternar('alumnos')} testid="avance-seccion-alumnos"
                  chip={t('groups.member_count', { count: g.alumnos })}>
                  <PorAlumno {...comun} angosto={angosto} abierto={alumnoAbierto}
                    alternar={(id) => setAlumnoAbierto((x) => (x === id ? null : id))}
                    plegados={plegados} alternarMotivo={(m) => setPlegados((p) => ({ ...p, [m]: !p[m] }))}
                    busqueda={busqueda} setBusqueda={setBusqueda} recordarTodos={recordarTodos}
                    enviandoTodos={enviandoTodos} />
                </SeccionPlegable>
              </div>
              <div id="avance-seccion-tabla">
                <SeccionPlegable titulo={t('groups.avance.seccion.tabla')} abierta={abiertas.tabla}
                  alternar={() => alternar('tabla')} testid="avance-seccion-tabla"
                  chip={t('groups.avance.publicaciones', { count: g.cards_publicadas })}>
                  <Tabla {...comun} pestana={pestana} setPestana={setPestana} globo={globo} setGlobo={setGlobo} angosto={angosto} />
                </SeccionPlegable>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
}

/** post-t7-2b (D1): el medidor del boton de regresar: el mismo boton en la disposicion de computadora, en una linea,
    oculto, inerte y sin ocupar lugar. Da B, su ancho natural, a la regla de la barra en cualquier disposicion (el boton
    de verdad cambia de letra y de relleno en el telefono). Remate: tambien lo lleva el esqueleto cuando ya conoce el
    nombre (llegando desde la pagina del grupo). */
function MedidorDelVolver({ nombre }) {
  return (
    <span aria-hidden="true" inert style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
      <span data-medidor-volver="si" data-testid="avance-medidor-volver" className="inline-flex items-center"
        style={{ ...estiloDelVolver(BOTON_VOLVER.computadora), width: 'max-content', textAlign: 'left', lineHeight: 1.3 }}>
        <span style={{ flexShrink: 0 }}>{'\u2190'}</span>
        <span>{nombre}</span>
      </span>
    </span>
  );
}

/** post-t7-2b (remate de D1 y D7): `useLocation` si el ruteo lo trae (las pruebas viejas doblan el ruteo sin el). */
const SIN_UBICACION = () => null;
const usarUbicacion = Ruteo.useLocation || SIN_UBICACION;

/** El subtitulo de la pagina ("12 alumnos, 2 publicaciones activas, ..."). post-t7-2b (D7): salio de `pintar` sin
    cambiar una letra, para que el esqueleto pinte el mismo (oculto) y reserve su alto real. */
function subtituloDe(t, g) {
  const caducadas = g.cards_caducadas ?? Math.max(0, (g.cards_publicadas || 0) - (g.cards_activas || 0));
  // la regla del cero: sin alumnos o sin cards activas, ese pedazo no sale ("0 alumnos")
  const piezasDelSubtitulo = [
    ...(g.alumnos > 0 ? [t('groups.avance.sub.alumnos', { count: g.alumnos })] : []),
    // t7-3 remate 8 (#591, texto 11, C123): sin activas, "ninguna publicacion activa"
    ...(g.cards_activas > 0 ? [t('groups.avance.sub.activas', { count: g.cards_activas })]
      : (g.cards_publicadas > 0 ? [t('groups.avance.sub.activas_ninguna')] : [])),
    ...(caducadas > 0 ? [t('groups.avance.sub.caducadas', { count: caducadas })] : []),
    t('groups.avance.sub.solo'),
  ].join(', ');
  // post-t7-1 (A4): sin alumnos y sin activas, el subtitulo abre con "ninguna publicacion activa": mayuscula inicial,
  // como el recuadro de la pagina del grupo. Ninguna palabra cambia.
  const abreConNinguna = !(g.alumnos > 0) && !(g.cards_activas > 0) && g.cards_publicadas > 0;
  return abreConNinguna ? piezasDelSubtitulo.charAt(0).toUpperCase() + piezasDelSubtitulo.slice(1) : piezasDelSubtitulo;
}

/** post-t7-2b (D7): el grupo tipico con que el esqueleto (que aun no tiene los datos) arma su subtitulo oculto: el del
    grupo demo (12 alumnos y 2 publicaciones activas, sin caducadas). Un grupo con caducadas puede partir un renglon
    mas que el esqueleto. */
export const GRUPO_DEL_ESQUELETO = { alumnos: 12, cards_activas: 2, cards_publicadas: 2, cards_caducadas: 0 };

/** t7-3 remate 2 (R7): el esqueleto de carga es la SILUETA de la pagina (la barra fija, el subtitulo, las tres
    respuestas con alturas parecidas a las reales y, debajo, las tres secciones plegadas), no cuatro tarjetas
    genericas: al llegar los datos no brinca. LAPIDA: murio `ListSkeleton rows={4}` en esta pagina. */
export const ALTOS_DEL_ESQUELETO = { telefono: [378, 506, 408], computadora: [402, 525, 408] };

/** post-t7-2c (D2): el alto que el esqueleto reserva para la barra de computadora: el MEDIDO de este grupo a este ancho
    e idioma (lo recuerda `recordarDelGrupo`), si lo hay; si no, la cuenta SIN el boton del chat en el renglon 2 (78.5),
    porque ese boton ya no va en la barra. LAPIDA: se pedia a `altoReservadoDeLaBarra` (`lib/esqueletoDelAvance.js`),
    que sin medida cuenta el boton del chat (95 con el chat encendido): la pagina subia 16.5 px al llegar los datos. */
function altoDeLaBarraDelEsqueleto(id, W, lang) {
  const g = recuerdoDelGrupo(id);
  const b = g && g.barra;
  if (b && b.lang === lang && Math.abs((b.W || 0) - W) < 1 && b.alto > 0) return b.alto;
  return altoDeLaBarraDeComputadora({ chat: false });
}

/* post-t7-2b (D7 y remate): el esqueleto recibe lo recordado de este grupo (`lib/esqueletoDelAvance.js`): su ultimo
   subtitulo en este idioma y el alto de su barra de computadora (el medido a este ancho, o la cuenta con el chat).
   LAPIDA: murio `SUBTITULOS_VISTOS` (un Map en memoria): ahora se recuerda tambien al recargar.
   post-t7-2c (D2 y D3): el renglon 2 de la barra de computadora ya no lleva el boton del chat, asi que el esqueleto le
   reserva el alto del titulo (19.5) y no el del boton (36); y el nombre del boton de regresar va en un renglon. LAPIDA:
   murio la prop `chat` (decidia 36 o 19.5). */
function EsqueletoDelAvance({ apilada, angosto, enBarra = !angosto, t, subtitulo, altoDeLaBarra, nombre = '',
  anchoDelBoton = null, altoDelTelefono = 0 }) {
  // post-t7-2b (remate de D1): en computadora y con el nombre del grupo, la barra del esqueleto se ARMA como la real
  // (relleno 8 + 8, el boton de regresar oculto con su nombre en el ancho que le toca, el hueco de 6 y el renglon 2: el
  // boton del chat de 36 o el titulo de 19.5): mide lo mismo aunque el nombre parta renglon dentro del boton
  const armada = enBarra && !!nombre;
  const M = MEDIDAS_DE_LA_BARRA_DE_COMPUTADORA;
  const bloque = (alto, ancho = '100%', extra = {}) => (
    <div aria-hidden="true" className="animate-pulse rounded-md bg-white/[0.06]" style={{ height: alto, width: ancho, ...extra }} />
  );
  // un renglon de texto: la caja del renglon real (su interlineado) con la barrita al centro
  const renglon = (caja, alto, ancho) => (
    <div style={{ height: caja, display: 'flex', alignItems: 'center' }}>{bloque(alto, ancho)}</div>
  );
  const altos = ALTOS_DEL_ESQUELETO[apilada ? 'telefono' : 'computadora'];
  return (
    <div data-testid="skeleton-avance" aria-busy="true">
      {/* las alturas son las medidas en Chrome con el chasis real: el titulo grande solo desde 768; en el telefono,
          el subtitulo en dos renglones y la leyenda en otros dos. t7-3 remate 3 (D): la barra es de DOS renglones
          (el boton de regresar y, debajo, "Avance del grupo. Datos de hoy"): 79 px en todos los anchos (medido:
          78.5); antes 60 en el telefono y 73 en la computadora. La leyenda va debajo del subtitulo donde la barra
          no la lleva (menos de 1024 de ventana). */}
      {/* t7-3 remate 8 (#585): en el telefono el boton mide 44 (antes 36): la barra crece 8 px; medida en Chrome con el
          nombre del demo, 86.5 (el esqueleto de antes, 79, dejaba un brinco de medio pixel) */}
      {/* post-t7-2b (remate de D1): en computadora, el alto REAL de la barra (`altoDeLaBarra`: el medido de este grupo a
          este ancho, o la cuenta con el boton del chat: 95 con el, 78.5 sin el). Antes, 79 siempre: con el chat la pagina
          bajaba 15.6 px al llegar los datos */}
      <div data-testid="avance-esqueleto-barra"
        style={{ ...(armada ? { paddingTop: M.relleno, paddingBottom: M.relleno }
          // Remate de post-t7-2c (15-oct): en el telefono, el alto recordado de la barra de ESTE grupo a este ancho e
          // idioma (el nombre puede ir en dos renglones), si lo hay. LAPIDA: siempre `ALTO_DE_LA_BARRA_TELEFONO`.
          : { minHeight: enBarra ? (altoDeLaBarra || 79) : (altoDelTelefono || ALTO_DE_LA_BARRA_TELEFONO) }), display: 'flex',
          flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start',
          gap: 6, borderBottomWidth: 1, borderBottomStyle: 'solid', borderBottomColor: '#1f1f1f' }}>
        {armada ? (
          <span style={{ position: 'relative', display: 'inline-flex', maxWidth: '100%' }}>
            <span data-testid="avance-esqueleto-volver" aria-hidden="true" inert className="inline-flex items-center"
              style={{ ...estiloDelVolver(BOTON_VOLVER.computadora), visibility: 'hidden', textAlign: 'left', lineHeight: 1.3,
                maxWidth: anchoDelBoton > 0 ? anchoDelBoton : '100%' }}>
              <span style={{ flexShrink: 0 }}>{'\u2190'}</span>
              <span style={UN_RENGLON}>{nombre}</span>
            </span>
            <span aria-hidden="true" className="animate-pulse rounded-md bg-white/[0.06]"
              style={{ position: 'absolute', inset: 0, borderRadius: 999 }} />
          </span>
        ) : bloque(enBarra ? BOTON_VOLVER.computadora.alto : BOTON_VOLVER.telefono.alto, 150, { borderRadius: 999 })}
        {armada ? (
          <div data-testid="avance-esqueleto-renglon-2" style={{ height: M.titulo, display: 'flex', alignItems: 'center' }}>
            {bloque(14, 200)}
          </div>
        ) : bloque(14, 200)}
      </div>
      {/* Remate de post-t7-2c (R4): el medidor va SIEMPRE que se conoce el nombre, no solo armada. Con la reserva = B
          entero, armar la barra (B sin medir: 220) y medir B podia desarmarla, y al desarmarse se iba el medidor: la
          barra del esqueleto iba y venia. LAPIDA: decia `armada &&`. */}
      {!!nombre && <MedidorDelVolver nombre={nombre} />}
      {/* post-t7-2b (D7): el titulo grande, con el MISMO corte que la pagina (la ventana de 768: el titulo es de la ventana,
          no del ancho de la caja). Antes se decidia con `angosto`, y a 768 con el menu lateral el esqueleto no lo
          reservaba: la pagina brincaba 44 px al llegar los datos (medido en Chrome con el demo) */}
      <div className="md:hidden" aria-hidden="true" style={{ height: 14 }} />
      <div className="hidden md:block" style={{ marginTop: 22, marginBottom: 2 }}>{renglon(34, 28, 260)}</div>
      {/* post-t7-2b (D7): el subtitulo reserva su alto REAL: un subtitulo oculto, con la letra de la pagina y el texto del
          ultimo subtitulo visto de este grupo (o, la primera vez, el de un grupo tipico, `GRUPO_DEL_ESQUELETO`), parte
          en los renglones que toque en cada ancho e idioma; encima, sus barritas. Antes, dos renglones fijos en el
          telefono y uno en la computadora */}
      <div data-testid="avance-esqueleto-subtitulo" style={{ marginBottom: angosto ? 8 : 18, position: 'relative', overflow: 'hidden' }}>
        {t ? (
          <>
            <p aria-hidden="true" style={{ fontSize: 14, visibility: 'hidden' }}>{subtitulo || subtituloDe(t, GRUPO_DEL_ESQUELETO)}</p>
            <div aria-hidden="true" style={{ position: 'absolute', inset: 0 }}>
              {renglon(21, 12, angosto ? '85%' : '55%')}{renglon(21, 12, angosto ? '45%' : '35%')}{renglon(21, 12, '30%')}
            </div>
          </>
        ) : (
          <>
            {renglon(21, 12, angosto ? '85%' : '55%')}
            {angosto && renglon(21, 12, '45%')}
          </>
        )}
      </div>
      {/* post-t7-1 (#595): con seis rotulos la leyenda del telefono parte en tres renglones a 320 (dos de 375 en
          adelante): el esqueleto reserva el alto de la leyenda REAL (oculta e inerte) y encima pinta sus barritas, asi
          que no brinca en ningun ancho ni idioma. Sin `t` (no deberia pasar), los dos renglones de antes. */}
      {!enBarra && (t ? (
        <div data-testid="avance-esqueleto-leyenda" style={{ marginBottom: 18, position: 'relative', overflow: 'hidden' }}>
          <span aria-hidden="true" inert style={{ visibility: 'hidden', display: 'block' }}>
            <Leyenda t={t} testid="avance-esqueleto-leyenda-silueta" abierta={null} alTocar={NADA} />
          </span>
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0 }}>
            {renglon(32, 10, '90%')}{renglon(32, 10, '70%')}{renglon(32, 10, '50%')}
          </div>
        </div>
      ) : (
        <div data-testid="avance-esqueleto-leyenda" style={{ marginBottom: 18 }}>
          {renglon(32, 10, '90%')}{angosto && renglon(32, 10, '50%')}
        </div>
      ))}
      {/* t7-3 remate 4 (C1): la barra del esqueleto usa la misma regla que la de la pagina, y para medir L hace
          falta la leyenda: va aqui, oculta, inerte y sin ocupar lugar (ni ancho que desborde la pagina) */}
      {t && (
        <span aria-hidden="true" inert style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
          <span style={{ display: 'block', width: 'max-content' }}>
            <Leyenda t={t} testid="avance-medidor-leyenda" abierta={null} alTocar={NADA} unaLinea />
          </span>
        </span>
      )}
      <div data-testid="avance-esqueleto-respuestas" style={apilada ? { display: 'flex', flexDirection: 'column', gap: 14 }
        : { display: 'grid', gridTemplateColumns: css('1fr', '1.15fr', '1fr'), gap: 14, alignItems: 'start' }}>
        {altos.map((alto, i) => (
          <div key={i} data-testid={`avance-esqueleto-respuesta-${i}`}
            style={{ ...CAPA, minHeight: alto, display: 'grid', gap: 10, alignContent: 'start' }}>
            {bloque(20, '65%')}
            {bloque(56)}
            {bloque(56)}
            {bloque(56)}
          </div>
        ))}
      </div>
      <div className="space-y-3" style={{ marginTop: 14 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} data-testid={`avance-esqueleto-seccion-${i}`} className={CAPA_DE_SECCION}
            style={{ height: 56, display: 'flex', alignItems: 'center', paddingLeft: 16, paddingRight: 16 }}>
            {bloque(18, 180)}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ======================================================================
   1. "QUIEN ESTA EN RIESGO?" (#542; con 3 o menos, por alumno: #546)
   ====================================================================== */

function datoDelRiesgo(t, tt, r) {
  const d = r.dato || {};
  if (r.motivo === 'nunca') return d.se_unio_hace_dias === null || d.se_unio_hace_dias === undefined ? '' : seUnioTexto(t, d.se_unio_hace_dias);
  if (r.motivo === 'dejo') return t('groups.avance.riesgo.dato_dejo', { count: d.dias_sin_practicar });
  if (r.motivo === 'le_cuestan') {
    if (d.ultima_practica_dias !== undefined && d.ultima_practica_dias !== null) {
      return tt('groups.avance.riesgo.dato_ultima', { cuando: cuandoDeDias(t, d.ultima_practica_dias) });
    }
    return t('groups.avance.riesgo.dato_practica', { count: d.preguntas_contestadas });
  }
  // t7-3 remate 8 (#589): "ultima practica hace N dias" (mas de 7) o "N preguntas contestadas"
  if (r.motivo === 'poco') {
    if (d.ultima_practica_dias !== undefined && d.ultima_practica_dias !== null) {
      return tt('groups.avance.riesgo.dato_ultima', { cuando: cuandoDeDias(t, d.ultima_practica_dias) });
    }
    return t('groups.avance.contestadas', { count: d.preguntas_contestadas || 0 });
  }
  return tt('groups.avance.riesgo.dato_atoro', { count: d.temas_sin_abrir, tema: d.tema });
}

function chipDelRiesgo(t, r) {
  if (r.motivo === 'le_cuestan') return t('groups.avance.riesgo.le_cuestan', { count: r.dato?.temas_en_rojo });
  return t(`groups.avance.riesgo.${r.motivo}`);
}

/** Con 3 alumnos o menos: un renglon por alumno en riesgo, como en la v2. */
function FilaDeRiesgo({ t, tt, r, alumno, recordados, enviando, recordar, abrirAlumno }) {
  return (
    <div data-testid={`avance-riesgo-${r.alumno_id}`}
      {...botonDe(() => abrirAlumno(r.alumno_id))} aria-expanded={undefined}
      className="border border-[#262626] hover:border-[#3a3a3a] transition-colors"
      style={{ display: 'grid', gridTemplateColumns: css('36px', 'minmax(0,1fr)', 'auto'), columnGap: 10, rowGap: 3,
        alignItems: 'center', background: '#181818', borderRadius: 12, ...relleno(10, 12), cursor: 'pointer' }}>
      <span style={{ gridRow: css('span', 2), alignSelf: 'center' }}><Iniciales texto={r.iniciales} /></span>
      <p style={{ fontSize: 15, fontWeight: 600, color: BLANCO, lineHeight: 1.2, ...PARTE_RENGLON }}
        title={r.nombre}>{r.nombre}</p>
      <BotonRecordar t={t} alumno={alumno} recordados={recordados} enviando={enviando} recordar={recordar}
        testid={`avance-riesgo-${r.alumno_id}-recordar`} anchoMinimo={false} />
      <div style={{ minWidth: 0, gridColumn: css(2, '/', -1) }}>
        <p style={{ fontSize: 12, color: GRIS }}>
          <span style={{ marginRight: 6 }}>
            <ChipDeMotivo motivo={r.motivo} testid={`avance-riesgo-${r.alumno_id}-chip`}>{chipDelRiesgo(t, r)}</ChipDeMotivo>
          </span>
          {/* t7-3 remate 7 (pasada 4): un espacio que solo oye el lector de pantalla (en pantalla separa el margen) */}
          <span className="sr-only"> </span>
          <span data-testid={`avance-riesgo-${r.alumno_id}-dato`}>{datoDelRiesgo(t, tt, r)}</span>
        </p>
      </div>
    </div>
  );
}

/** t7-2d remate (#552): cuantos alumnos se ven en la lista abierta de un motivo antes de deslizar. */
export const RENGLONES_A_LA_VISTA = 5;
/** El alto de un renglon de alumno en esa lista (px). */
export const ALTO_RENGLON_MOTIVO = 40;

/** #552: un renglon por motivo con gente, DESPLEGABLE. Cerrado: el chip en plural (singular si es
    uno), "x de y" y "Ver quienes"; ningun nombre. Abierto (uno a la vez): la lista del motivo (5 a la
    vista, el resto se desliza), cada alumno abre SU detalle, y al final "Recordar a todos ellos".
    La cabecera es UN boton; la lista es hermana de la cabecera; el recuadro es un div cuyo clic (en una
    zona vacia) abre y cierra, y que ignora los clics que vienen de un boton de adentro. */
function FilaDeMotivo({ t, m, y, abierta, alternar, abrirAlumno, alumnoPorId, recordados, enviandoTodos, recordarTodos }) {
  const id = `avance-motivo-${m.motivo}`;
  const listaRef = useRef(null);
  const [queda, setQueda] = useState(true);
  const alumnos = (m.alumnos || []).map((uid) => alumnoPorId[uid]).filter(Boolean);
  const desliza = alumnos.length > RENGLONES_A_LA_VISTA;
  const medir = useCallback(() => {
    const l = listaRef.current;
    // sin maquetado (clientHeight 0) no hay nada que medir: el degradado se queda
    if (l && l.clientHeight > 0) setQueda(l.scrollHeight - l.scrollTop - l.clientHeight > 1);
  }, []);
  useEffect(() => { if (abierta && desliza) medir(); }, [abierta, desliza, medir]);
  const alRecuadro = (e) => {
    const boton = e.target && typeof e.target.closest === 'function' ? e.target.closest('button') : null;
    if (boton && e.currentTarget.contains(boton)) return;
    alternar(m.motivo);
  };
  return (
    <div data-testid={id} data-motivo={m.motivo} onClick={alRecuadro}
      className={`border ${abierta ? 'border-[#FF6B00]' : 'border-[#262626] hoverable:border-[#FF6B00]'} transition-colors`}
      style={{ background: '#181818', borderRadius: 12, ...relleno(10, 12), cursor: 'pointer' }}>
      <button type="button" data-testid={`${id}-cabecera`} aria-expanded={abierta} aria-controls={`${id}-lista`}
        onClick={soloEste(() => alternar(m.motivo))} className="cursor-pointer"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 8, width: '100%', minWidth: 0, background: 'transparent',
          padding: 0, textAlign: 'left', position: 'relative' }}>
        <AreaDeToque />
        <span style={{ minWidth: 0 }}>
          <ChipDeMotivo motivo={m.motivo} testid={`${id}-chip`}>{t(`groups.avance.motivo.${m.motivo}`, { count: m.x })}</ChipDeMotivo>
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
          <span data-testid={`${id}-cuenta`} style={{ fontSize: 14, color: BLANCO, whiteSpace: 'nowrap' }}>
            <strong style={{ fontWeight: 700 }}>{t('groups.avance.x_de_y', { x: m.x, y })}</strong>
          </span>
          <span data-testid={`${id}-ver`} className="inline-flex items-center"
            style={{ gap: 2, fontSize: 12, fontWeight: 600, color: NARANJA, whiteSpace: 'nowrap' }}>
            {abierta ? t('grupos_alumno.aria_cerrar') : t('groups.avance.ver_quienes', { count: m.x })}
            <ChevronDown aria-hidden="true" className={`w-3.5 h-3.5 shrink-0 transition-transform ${abierta ? 'rotate-180' : ''}`} />
          </span>
        </span>
      </button>
      {abierta && (
        <div style={{ marginTop: 8, position: 'relative', borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: '#262626' }}>
          {/* el borde vive en el envoltorio: la lista mide 5 x 40 = 200 exactos y los 5 renglones caben enteros */}
          <div id={`${id}-lista`} data-testid={`${id}-lista`} ref={listaRef} onScroll={desliza ? medir : undefined}
            style={{ maxHeight: RENGLONES_A_LA_VISTA * ALTO_RENGLON_MOTIVO, ...(desliza ? { overflowY: 'auto' } : {}) }}>
            {alumnos.map((a) => (
              <button key={a.id} type="button" data-testid={`${id}-alumno-${a.id}`} onClick={soloEste(() => abrirAlumno(a.id))}
                title={a.nombre} className="group flex items-center w-full"
                style={{ height: ALTO_RENGLON_MOTIVO, gap: 10, background: 'transparent', padding: 0, textAlign: 'left' }}>
                <Iniciales texto={a.iniciales} lado={28} />
                <span className="text-white group-hover:text-[#FF6B00]"
                  style={{ flex: 1, fontSize: 14, fontWeight: 600, ...UN_RENGLON }}>{a.nombre}</span>
                <ChevronRight aria-hidden="true" className="w-4 h-4 shrink-0 text-[#6a6a6a] group-hover:text-[#FF6B00]" />
              </button>
            ))}
          </div>
          {desliza && queda && (
            <span aria-hidden="true" data-testid={`${id}-degradado`}
              style={{ position: 'absolute', left: 0, right: 0, top: RENGLONES_A_LA_VISTA * ALTO_RENGLON_MOTIVO - 16,
                height: 16, pointerEvents: 'none', background: css('linear-gradient(to', 'bottom,', 'rgba(24,24,24,0),', '#181818)') }} />
          )}
          {alumnos.length >= 2 && (
            <div style={{ marginTop: 10 }}>
              <BotonTodos t={t} clave={m.motivo} alumnos={alumnos} recordados={recordados} enviandoTodos={enviandoTodos}
                recordarTodos={recordarTodos} testid={`${id}-recordar-todos`} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ColRiesgo({ t, tt, datos, recordados, enviando, recordar, abrirAlumno, verAlumnos, motivoAbierto, alternarMotivo,
  alumnoPorId, recordarTodos, enviandoTodos }) {
  const y = datos.grupo.alumnos;
  const nadie = (datos.vistazo?.practicaron ?? 1) === 0;
  const conNombres = datos.con_nombres === true;
  const porAlumno = nadie ? [] : (datos.riesgo || []);
  const motivos = nadie ? [] : (datos.riesgo_por_motivo || []);
  const total = datos.riesgo_total ?? porAlumno.length;
  const conCuenta = !conNombres && motivos.length > 0 && total > 0;
  const titulo = conCuenta
    ? <ConNegritas texto={t('groups.avance.riesgo_titulo_de', { r: total, y })} />
    : t('groups.avance.riesgo_titulo');
  const hay = conNombres ? porAlumno.length > 0 : motivos.length > 0;
  const unico = y === 1 ? (datos.alumnos || [])[0] : null;
  return (
    <Respuesta testid="avance-col-riesgo" guia={AMARILLO} titulo={titulo} ayuda={t('groups.avance.ayuda_riesgo')}>
      {!hay ? (
        <Vacio testid="avance-riesgo-vacio">{t(nadie ? 'groups.avance.nadie_todavia' : 'groups.avance.nadie_riesgo')}</Vacio>
      ) : (
        <div className="space-y-2">
          {conNombres
            ? porAlumno.map((r) => (
              <FilaDeRiesgo key={r.alumno_id} t={t} tt={tt} r={r} recordados={recordados} enviando={enviando}
                recordar={recordar} abrirAlumno={abrirAlumno}
                alumno={alumnoPorId[r.alumno_id] || { id: r.alumno_id, recordado_hoy: r.recordado_hoy }} />
            ))
            : motivos.map((m) => (
              <FilaDeMotivo key={m.motivo} t={t} m={m} y={y} abierta={motivoAbierto === m.motivo} alternar={alternarMotivo}
                abrirAlumno={abrirAlumno} alumnoPorId={alumnoPorId} recordados={recordados} enviandoTodos={enviandoTodos}
                recordarTodos={recordarTodos} />
            ))}
        </div>
      )}
      {unico ? (
        <PieNaranja testid="avance-ver-alumnos" onClick={() => abrirAlumno(unico.id)}>
          {tt('groups.avance.ver_detalle_de', { nombre: unico.nombre })}
        </PieNaranja>
      ) : (
        <PieNaranja testid="avance-ver-alumnos" onClick={verAlumnos}>{t('groups.avance.ver_alumnos', { count: y })}</PieNaranja>
      )}
    </Respuesta>
  );
}

/* ======================================================================
   2. "QUE NO ESTAN ENTENDIENDO?" (#543)
   ====================================================================== */

function textoFallan(t, n, m) {
  if (n === 1 && m === 1) return t('groups.avance.la_fallo_el_unico');
  return t('groups.avance.la_fallan_q', { count: n, m });
}

/** "La fallan n de los m que la contestaron." o, con 3 alumnos o menos, con los nombres (#546). */
function textoFallanDe(t, tt, q, conNombres) {
  const nombres = q.fallan_nombres;
  if (conNombres && nombres && nombres.length) {
    if (q.m === 1 && q.n === 1) return tt('groups.avance.nombres.la_fallo', { nombre: nombres[0] });
    return tt('groups.avance.nombres.la_fallan', { count: nombres.length, nombres: nombres.join(', '), m: q.m });
  }
  return textoFallan(t, q.n, q.m);
}

/** t7-2d remate (#551): con 3 alumnos o menos el parentesis de "Eligen" dice QUIENES: la MISMA
    plantilla ratificada, cortada antes de "({{k}} de {{n}})", sin llave nueva. */
function conNombresEnElParentesis(tt, llave, opcion, nombres) {
  const base = tt(llave, { opcion, k: 0, n: 0 });
  return `${base.slice(0, base.lastIndexOf(' ('))} (${nombres.join(', ')})`;
}

/** t7-3 remate 7 (#582 D): el rotulo del renglon de la respuesta equivocada, con sus hermanas de numero (las mismas
    reglas del servidor, `avance._llave_de_eligen`): dos o mas opciones, "Respuestas equivocadas que eligen:"; UN solo
    nombre en el parentesis, "Respuesta equivocada que elige:"; si no, "Respuesta equivocada que eligen:". */
function llaveDeEligen(e) {
  if (e.length >= 2) return 'groups.avance.eligen_varias';
  if ((e[0].nombres || []).length === 1) return 'groups.avance.eligen_uno';
  return 'groups.avance.eligen';
}

/** t7-3 remate 7 (firma h): los RENGLONES de la respuesta equivocada (letra por letra `avance.renglones_eligen` del
    servidor). "La mayoria elige ..." va SOLO, sin parentesis de conteo; si hay otra respuesta equivocada con su conteo,
    va en su propio renglon con el rotulo que ya existe (antes iba pegada a la de la mayoria con una coma). */
function renglonesEligen(tt, q) {
  const e = q.eligen || [];
  if (!e.length) return [];
  if (e[0].nombres) {
    return [e.map((x, i) => conNombresEnElParentesis(tt, i ? 'groups.avance.eligen_otra' : llaveDeEligen(e),
      x.opcion, x.nombres)).join(', ')];
  }
  const renglon = (llave, opciones) => [tt(llave, { opcion: opciones[0].opcion, k: opciones[0].k, n: q.n }),
    ...opciones.slice(1).map((otra) => tt('groups.avance.eligen_otra', { opcion: otra.opcion, k: otra.k, n: q.n }))]
    .join(', ');
  if (!q.mayoria) return [renglon(llaveDeEligen(e), e)];
  const otras = e.slice(1);
  return [tt('groups.avance.mayoria_elige', { opcion: e[0].opcion }),
    ...(otras.length ? [renglon(otras.length >= 2 ? 'groups.avance.eligen_varias' : 'groups.avance.eligen', otras)] : [])];
}

function nombreDificultad(t, dif) {
  return dif ? t(`groups.avance.dificultad.${dif}`) : '';
}

/** t7-3 remate 7 (#582 D): los rotulos de la respuesta equivocada y de la correcta, en su color y con peso 500 (la
    opcion sigue en el color del cuerpo de su caja). Los mismos valores del correo (`correo_semanal.py`). */
const TONO_DEL_ROTULO = { equivocada: '#E8807C', correcta: '#6CC08B' };

/** t7-3 remate 2 (R7): "Rotulo: resto" en su propio renglon, con el rotulo ("Por que esta mal:") en negritas
    claras. t7-3 remate 7 (#582 D): con `tono`, el rotulo de la respuesta equivocada o de la correcta va en su color
    y peso 500. */
/** t7-3 remate 7 (regla 2 de #581): el "(k de n)" de un renglon no se parte entre dos renglones (espacios duros). */
function sinPartirLosConteos(texto) {
  return texto.replace(/\((\d+) (de|of) (\d+)\)/g, '($1\u00a0$2\u00a0$3)');
}

/** post-t7-1 (A5): "4 de 9" / "4 of 9" / "ninguna de 9" sin parentesis tampoco se parte (espacios duros). */
export function sinPartirElConteo(texto) {
  return String(texto).replace(/(\d+|ninguna|none) (de|of) (\d+)/g, '$1\u00a0$2\u00a0$3');
}

function ConRotulo({ texto: crudo, testid, tono }) {
  const texto = sinPartirLosConteos(crudo);
  const i = texto.indexOf(': ');
  if (i < 0) return <span data-testid={testid} style={{ display: 'block' }}>{texto}</span>;
  const estilo = tono ? { color: TONO_DEL_ROTULO[tono], fontWeight: 500 } : { color: BLANCO, fontWeight: 700 };
  return (
    <span data-testid={testid} style={{ display: 'block' }}>
      <strong data-rotulo={tono || undefined} style={estilo}>{texto.slice(0, i + 1)}</strong>
      {texto.slice(i + 1)}
    </span>
  );
}

/** t7-3 remate 7 (#582 D): "Respuesta correcta: "x"", o nada si la pregunta no la trae (un servidor de antes). */
function textoCorrecta(tt, q) {
  return q && q.correcta ? tt('groups.avance.correcta', { opcion: q.correcta }) : '';
}

/** La caja de una pregunta dentro de "Para tu clase" (seccion 3.2). */
function CajaPregunta({ t, tt, q, rotulo, discutan, testid, conNombres }) {
  const eligen = renglonesEligen(tt, q);
  const porQue = q.por_que ? tt('groups.avance.por_que', { razon: q.por_que }) : '';
  const correcta = textoCorrecta(tt, q);
  return (
    <div data-testid={testid} style={{ ...CAJA, ...relleno(10, 12), display: 'grid', gap: 4, fontSize: 13 }}>
      <p data-testid={`${testid}-rotulo`}
        style={{ color: NARANJA, fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', ...PARTE_RENGLON }}>
        {rotulo}
      </p>
      <p data-testid={`${testid}-pregunta`} style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, color: BLANCO, ...PARTE_RENGLON }}>
        {`"${q.texto}"`}
      </p>
      {/* t7-3 remate 7 (firma g y hermanos): la linea de "La fallan" va como en la caja de arranque (12 px en gris) y, con
          3 alumnos o menos, con los nombres (antes 15 px en gris claro y sin nombres) */}
      <p data-testid={`${testid}-fallan`} style={{ fontSize: 12, color: GRIS, ...PARTE_RENGLON }}>
        <ConNegritas texto={textoFallanDe(t, tt, q, conNombres) + (discutan ? ` ${t('groups.avance.discutan')}` : '')} />
      </p>
      {(eligen.length > 0 || porQue || correcta) && (
        <p style={LECTURA}>
          {eligen.map((x, i) => <ConRotulo key={i} testid={i ? `${testid}-eligen-${i}` : `${testid}-eligen`} texto={x} tono="equivocada" />)}
          {porQue && <ConRotulo testid={`${testid}-por-que`} texto={porQue} />}
          {correcta && <ConRotulo testid={`${testid}-correcta`} texto={correcta} tono="correcta" />}
        </p>
      )}
    </div>
  );
}

function Pista({ color, testid, children, estilo }) {
  const grande = !!estilo;
  return (
    <p data-testid={testid} className="flex items-start" style={{ gap: 8, fontSize: 12, color: GRIS, ...(estilo || {}) }}>
      <span data-cuadrito="si" aria-hidden="true"
        style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0, marginTop: grande ? 7 : 3 }} />
      <span>{children}</span>
    </p>
  );
}

/** t7-3 remate 8 (#586): "Revisa antes: Y." y UNA oracion (letra por letra `avance_textos.frase_revisa_antes`): "Es
    la base de X, ..." con el mapa, "Va antes en el material, ..." sin el; "a n de los r alumnos ..." o, si son todos, "a
    los r alumnos ...". LAPIDA: murio el contraste escrito ("De los r que fallan X, n tambien la fallan; de los otros s,
    k."): sigue decidiendo si hay "Revisa antes", pero ya no se pinta. Con un servidor de antes (sin `n` o `r`), el
    texto que manda el servidor. */
function textoRevisa(t, tt, ra, nombreX) {
  if (ra.n === undefined || ra.r === undefined) return ra.texto || '';
  const cola = `groups.avance.${ra.base === 'mapa' ? 'revisa_mapa' : 'revisa_orden'}${ra.n === ra.r ? '_todos' : ''}`;
  // t7-3 remate 8 (instrumentos, A4): su "n de los r" no se parte entre dos renglones (espacios duros)
  return sinPartirElDeLos([tt('groups.avance.revisa_antes', { tema: ra.tema }),
    tt(cola, { X: nombreX, n: ra.n, r: ra.r })].join(' '));
}

/** t7-3 remate 8 (instrumentos, A4): "2 de los 3" / "2 of the 3" en una pieza (espacios duros). */
export function sinPartirElDeLos(texto) {
  return texto.replace(/(\d+) (de los|of the) (\d+)/g, (_m, n, de, r) => `${n}\u00a0${de.replace(' ', '\u00a0')}\u00a0${r}`);
}

/** La frase de dificultad (seccion 2.6), UNA POR RENGLON, letra por letra `avance_textos.lineas_dificultad` del
    servidor (lo fija una prueba). RE-ANCLADA EN t7-3 remate 8 (#586, Carlos leyo "las dificiles las fallan 3" como "las
    aciertan 7"): cada renglon dice su PROPIO "de cuantos" ("Las dificiles: las fallan 3 de los 4 que las
    contestaron."); sale solo si esa dificultad la contestaron 3 o mas y su conteo es mayor que cero (el "nadie" de las
    faciles, solo con "Fallan hasta las faciles"); al final, la conclusion. LAPIDA: murio el arranque "De los {{p}} que
    practicaron la publicacion:". Con un servidor de antes (sin los denominadores), los renglones que mande el servidor
    tal como vengan. */
export const MINIMO_CONTESTARON = 3;
function lineasDificultad(t, d) {
  if (!d || !d.frase) return [];
  if (d.faciles_contestaron === undefined) return d.lineas || [];
  const lineas = [];
  [['faciles', d.faciles_aciertan, d.faciles_contestaron, 'na'], ['medias', d.medias_fallan, d.medias_contestaron, 'nb'],
    ['dificiles', d.dificiles_fallan, d.dificiles_contestaron, 'nc']].forEach(([llave, n, contestaron, de]) => {
    if (contestaron < MINIMO_CONTESTARON) return;
    if (n) lineas.push(t(`groups.avance.dificultad_lineas.${llave}`, { count: n, [de]: contestaron }));
    else if (llave === 'faciles' && d.frase === 'desde_cero') {
      lineas.push(t('groups.avance.dificultad_lineas.faciles_nadie', { na: contestaron }));
    }
  });
  lineas.push(t(d.frase === 'aplicar' ? 'groups.avance.dif_aplicar' : 'groups.avance.dif_desde_cero'));
  return lineas;
}

/** Los renglones de la frase de dificultad, cada uno en su bloque. */
function Renglones({ lineas }) {
  return lineas.map((l, i) => <span key={i} data-renglon="si" style={{ display: 'block' }}>{l}</span>);
}

/** Las paginas del tema en su documento. t7-3 remate 2 (R4, Carlos 30-sep): "{{tema}} se encuentra en
    la pagina {{a}} del documento {{documento}}." / "... en las paginas {{a}} a {{b}} del documento ...";
    antes "Esta en {{documento}}, paginas {{a}} a {{b}}.". Audio o video (sin paginas): no sale. */
function textoPaginas(tt, f) {
  if (f.pagina_inicio === null || f.pagina_inicio === undefined || !f.documento) return '';
  if (f.pagina_fin === f.pagina_inicio || f.pagina_fin === null || f.pagina_fin === undefined) {
    return tt('groups.avance.esta_en_pagina', { tema: f.tema, documento: f.documento, a: f.pagina_inicio });
  }
  return tt('groups.avance.esta_en_paginas', { tema: f.tema, documento: f.documento, a: f.pagina_inicio, b: f.pagina_fin });
}

/** "Ver como explicarla": lo demas de "Para tu clase" dentro de la tarjeta del tema, y las
    paginas al final. En amarillo solo "Eligen" y "Por que esta mal". */
function piezasDeExplicarla(t, tt, datos, f, completo) {
  const q = f.pregunta;
  const tema = temaDeLaCard(datos, f.card_id, f.tema_id) || {};
  const eligen = q ? renglonesEligen(tt, q) : [];
  const porQue = q && q.por_que ? tt('groups.avance.por_que', { razon: q.por_que }) : '';
  const correcta = textoCorrecta(tt, q);
  const piezas = [];
  if (q && completo) piezas.push(['discutan', t('groups.avance.discutan')]);
  // t7-3 remate 7 (#582 D): la equivocada, "Por que esta mal" y, al final, la correcta
  if (eligen.length || porQue || correcta) piezas.push(['eligen', { eligen, porQue, correcta }]);
  if (!completo) return piezas;
  if (tema.dificil) piezas.push(['dificil', tema.dificil]);
  if (tema.revisa_antes) piezas.push(['revisa', textoRevisa(t, tt, tema.revisa_antes, f.tema)]);
  // LAPIDA t7-3 remate 2 (R6, #566): murio la pregunta sospechosa ("Revisa esta pregunta: ... puede estar mal
  // planteada"). El modulo la sigue calculando (la lee la ficha del chat, intocable); aqui no se pinta.
  const dif = lineasDificultad(t, tema.dificultad_resumen);
  if (dif.length) piezas.push(['dificultad', dif]);
  const paginas = textoPaginas(tt, f);
  if (paginas) piezas.push(['paginas', paginas]);
  return piezas;
}

function Explicarla({ t, tt, piezas, conNombres }) {
  return (
    <div data-testid="avance-explicarla" onClick={(e) => e.stopPropagation()} role="presentation"
      style={{ marginTop: 10, display: 'grid', gridTemplateColumns: 'minmax(0,1fr)', gap: 8, cursor: 'auto',
        alignContent: 'start' }}>
      {piezas.map(([clave, valor]) => {
        const testid = `avance-explicarla-${clave}`;
        if (clave === 'dificil') {
          return (<CajaPregunta key={clave} t={t} tt={tt} q={valor} testid={testid} rotulo={t('groups.avance.la_dificil')}
            conNombres={conNombres} />);
        }
        // t7-3 remate 2 (R7): todo el bloque en gris claro de 15 px con interlineado 1.5 (antes 12 px en #a0a0a0)
        // post-t7-2b (D7): un nombre de archivo sin espacios parte por sus guiones bajos y su punto (`conCortesSuaves`)
        if (clave === 'revisa') {
          return <Pista key={clave} color={AMARILLO} testid={testid} estilo={LECTURA}>{conCortesSuaves(valor)}</Pista>;
        }
        if (clave === 'dificultad') {
          return <p key={clave} data-testid={testid} style={LECTURA}><Renglones lineas={valor} /></p>;
        }
        if (clave === 'eligen') {
          return (
            <p key={clave} data-testid={testid} style={LECTURA}>
              {valor.eligen.map((x, i) => <ConRotulo key={i} texto={x} tono="equivocada" />)}
              {valor.porQue && <ConRotulo texto={valor.porQue} />}
              {valor.correcta && <ConRotulo texto={valor.correcta} tono="correcta" />}
            </p>
          );
        }
        return <p key={clave} data-testid={testid} style={LECTURA}>{conCortesSuaves(valor)}</p>;
      })}
    </div>
  );
}

/** La tarjeta del tema de la portada (el principal y el de la cascada): el tema entero, debajo SOLO el
    nombre de la publicacion (t7-3 remate 2, R5: en un renglon, con "..." solo si no cabe en todo el
    ancho; el documento ya no va aqui, vive en la linea de las paginas, R4), su cuenta, la barra de cinco
    tramos y lo que va dentro. */
function TarjetaDelTema({ f, cuenta, conNombres, children }) {
  return (
    <div data-testid="avance-tema-principal" tabIndex={-1} style={{ ...TARJETA, padding: 12 }}>
      {/* t7-3 remate 5 (C1): el nombre de la publicacion va en su PROPIO renglon, debajo del renglon del tema con
          su conteo, y ocupa todo el ancho interior de la tarjeta (antes compartia la columna angosta del tema). Con
          nombres, una sola columna: tema, publicacion y la cuenta debajo, como antes. */}
      {/* t7-3 remate 7 (regla 2 de #581): sin nombres, el renglon se reparte con flex: si el tema y su cuenta no caben
          juntos, la cuenta baja de renglon y el nombre parte SOLO entre palabras (antes la cuenta, que no parte, le
          quitaba ancho y el nombre se partia a media palabra: "Anualida-des" a 1100). Una palabra mas larga que la
          tarjeta entera si se parte (no cabe de otro modo). */}
      <div style={conNombres ? { display: 'grid', gap: 2 }
        : { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 8, rowGap: 2 }}>
        <p data-testid="avance-tema-principal-nombre" title={f.tema}
          style={{ fontSize: 15, fontWeight: 600, color: BLANCO, lineHeight: 1.3, minWidth: 0, ...PARTE_RENGLON,
            ...(conNombres ? {} : { flex: css('1', '1', 'auto'), maxWidth: '100%' }) }}>{f.tema}</p>
        {cuenta && !conNombres ? (
          <p data-testid="avance-tema-principal-cuenta"
            style={{ fontSize: 13, color: GRIS, whiteSpace: 'nowrap', marginLeft: 'auto' }}>
            {cuenta}
          </p>
        ) : null}
        {/* t7-3 remate 4 (D1): ENTERO y parte renglon (murio la excepcion R5 del remate 2: un renglon con "...") */}
        <p data-testid="avance-tema-principal-de" title={f.card}
          style={{ fontSize: 12, color: GRIS, ...PARTE_RENGLON,
            ...(conNombres ? {} : { flexBasis: '100%' }) }}>{f.card}</p>
        {cuenta && conNombres ? (
          <p data-testid="avance-tema-principal-cuenta" style={{ fontSize: 13, color: GRIS, marginTop: 4 }}>
            {cuenta}
          </p>
        ) : null}
      </div>
      {/* t7-3 remate 7 (firma g): con 3 alumnos o menos la portada va con nombres y SIN barras */}
      {!conNombres && <div style={{ marginTop: 8 }}><Barra dist={f.dist} alto={8} testid="avance-tema-principal-barra" /></div>}
      {children}
    </div>
  );
}

/** La caja de la pregunta A LA VISTA: rotulo naranja, la pregunta ENTERA y "La fallan n de los m que
    la contestaron.". LAPIDA t7-3 remate 2 (R2): murio el recorte a 3 renglones (2 en el telefono). */
function CajaALaVista({ t, tt, q, rotulo, conNombres }) {
  return (
    <div data-testid="avance-pregunta" style={{ ...CAJA, ...relleno(10, 12), marginTop: 10, display: 'grid', gap: 4 }}>
      <p data-testid="avance-pregunta-rotulo"
        style={{ color: NARANJA, fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
        {rotulo}
      </p>
      <p data-testid="avance-pregunta-texto" title={q.texto}
        style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, color: BLANCO, ...PARTE_RENGLON }}>
        {`"${q.texto}"`}
      </p>
      <p data-testid="avance-pregunta-fallan" style={{ fontSize: 12, color: GRIS }}>
        <ConNegritas texto={textoFallanDe(t, tt, q, conNombres)} />
      </p>
    </div>
  );
}

function ColTemas({ t, tt, datos, elegido, elegir, explicar, setExplicar, verPorCard, angosto }) {
  const y = datos.grupo.alumnos;
  const cascada = datos.cascada || { estado: 'sin_datos', tema: null };
  const estado = cascada.estado;
  const conNombres = datos.con_nombres === true;
  const idDe = (f) => `${f.card_id}--${f.tema_id}`;
  const lista = estado === 'rojo' && cascada.tema ? [cascada.tema, ...(datos.otros_temas_rojos || [])] : [];
  const f = estado === 'rojo' ? (lista.find((x) => idDe(x) === elegido) || lista[0]) : cascada.tema;
  const otros = lista.filter((x) => x !== f);
  const nombresDe = (lista2) => listaDeNombres(t, lista2);

  let cuerpo;
  if (estado === 'sin_datos' || (!f && estado !== 'verde')) {
    cuerpo = <Vacio testid="avance-temas-vacio">{t('groups.avance.ningun_tema_datos')}</Vacio>;
  } else if (estado === 'verde') {
    cuerpo = (
      <div style={{ ...TARJETA, padding: 12, display: 'grid', gap: 4 }}>
        <p data-testid="avance-cascada-verde" style={{ fontSize: 14, color: BLANCO, fontWeight: 600 }}>{t('groups.avance.todo_verde')}</p>
        <p data-testid="avance-cascada-avanzar" style={{ fontSize: 13, color: GRIS }}>{t('groups.avance.avanzar')}</p>
      </div>
    );
  } else if (estado === 'contorno') {
    const texto = conNombres && f.va_bien_nombres
      ? tt('groups.avance.nombres.contorno', { count: f.va_bien_nombres.length, tema: f.tema, nombres: nombresDe(f.va_bien_nombres) })
      : tt('groups.avance.contorno', { count: f.va_bien, tema: f.tema, y });
    cuerpo = (
      <>
        <p data-testid="avance-a-nadie-le-cuesta" style={{ fontSize: 13, color: GRIS, marginBottom: 8 }}>{t('groups.avance.a_nadie_le_cuesta')}</p>
        <TarjetaDelTema f={f} conNombres={conNombres}>
          <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
            <p data-testid="avance-cascada-contorno" style={{ fontSize: 13, color: BLANCO }}><ConNegritas texto={texto} /></p>
            <p data-testid="avance-cascada-pideles" style={{ fontSize: 13, color: GRIS }}>
              {t('groups.avance.pideles', { count: conNombres && f.va_bien_nombres ? f.va_bien_nombres.length : f.va_bien })}
            </p>
          </div>
        </TarjetaDelTema>
      </>
    );
  } else {
    const q = f.pregunta;
    const amarillo = estado === 'amarillo';
    let cuenta;
    if (amarillo) {
      cuenta = conNombres && f.medias_nombres
        ? tt('groups.avance.nombres.van_a_medias', { count: f.medias_nombres.length, nombres: nombresDe(f.medias_nombres) })
        : <ConNegritas texto={t('groups.avance.van_a_medias', { count: f.a, y })} />;
    } else {
      cuenta = conNombres && f.le_cuesta_nombres
        ? tt('groups.avance.nombres.le_cuesta', { count: f.le_cuesta_nombres.length, nombres: nombresDe(f.le_cuesta_nombres) })
        : <ConNegritas texto={t('groups.avance.le_cuesta_a', { count: f.r, y })} />;
    }
    const rotulo = q ? tt(amarillo ? 'groups.avance.la_que_mas_fallan' : 'groups.avance.empieza',
      { dificultad: nombreDificultad(t, q.dificultad) }) : '';
    const piezas = piezasDeExplicarla(t, tt, datos, f, !amarillo);
    cuerpo = (
      <>
        {amarillo && (
          <p data-testid="avance-a-nadie-le-cuesta" style={{ fontSize: 13, color: GRIS, marginBottom: 8 }}>{t('groups.avance.a_nadie_le_cuesta')}</p>
        )}
        <TarjetaDelTema f={f} cuenta={cuenta} conNombres={conNombres}>
          {q ? <CajaALaVista t={t} tt={tt} q={q} rotulo={rotulo} conNombres={conNombres} /> : (
            <div style={{ marginTop: 10, display: 'grid', gap: 4 }}>
              <p data-testid="avance-sin-pregunta" style={{ fontSize: 13, color: GRIS }}>{t('groups.avance.sin_pregunta')}</p>
              {f.p >= 1 && f.p < 3 && (
                <p data-testid="avance-pocos" style={{ fontSize: 12, color: GRIS }}>
                  {/* t7-3 remate 8 (C48, C78): con UNA persona, la hermana en singular */}
                  {conNombres && f.practicaron_nombres
                    ? tt(f.practicaron_nombres.length === 1 ? 'groups.avance.nombres.pocos_one' : 'groups.avance.nombres.pocos',
                      { nombres: nombresDe(f.practicaron_nombres) })
                    : t(f.p === 1 ? 'groups.avance.pocos_one' : 'groups.avance.pocos', { p: f.p, y })}
                </p>
              )}
            </div>
          )}
          {piezas.length > 0 && (
            <button type="button" data-testid="avance-ver-explicarla" aria-expanded={explicar}
              onClick={() => setExplicar((v) => !v)} className="hover:underline inline-flex items-center"
              style={{ marginTop: 10, gap: 4, color: NARANJA, fontSize: 13, fontWeight: 600, background: 'transparent',
                position: 'relative' }}>
              <AreaDeToque />
              {t(explicar ? 'groups.avance.ocultar_como_explicarla' : 'groups.avance.ver_como_explicarla')}
              <ChevronDown aria-hidden="true" className={`w-3.5 h-3.5 shrink-0 transition-transform ${explicar ? 'rotate-180' : ''}`} />
            </button>
          )}
          {explicar && piezas.length > 0 && <Explicarla t={t} tt={tt} piezas={piezas} conNombres={conNombres} />}
        </TarjetaDelTema>
        {otros.length > 0 && (
          <div data-testid="avance-otros-rojos" style={{ marginTop: 12 }}>
            <p data-testid="avance-otros-rojos-titulo"
              style={{ fontSize: 12, color: GRIS, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 6 }}>
              {t('groups.avance.otros_temas_rojos')}
            </p>
            <div style={{ display: 'grid', gap: 6 }}>
              {otros.map((o) => {
                const id = `avance-otro-${idDe(o)}`;
                return (
                  <div key={id} data-testid={id} data-tema={o.tema} {...botonDe(() => elegir(idDe(o)))} aria-expanded={undefined}
                    className="border border-[#262626] hover:border-[#3a3a3a] transition-colors"
                    style={{ background: '#181818', borderRadius: 10, ...relleno(8, 10), cursor: 'pointer', display: 'grid', gap: 6 }}>
                    {/* t7-3 remate 7 (regla 2): como el tema principal, si el tema y su cuenta no caben, la cuenta baja */}
                    <div style={conNombres ? { display: 'grid', gap: 2 }
                      : { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', columnGap: 8, rowGap: 2 }}>
                      <span title={o.tema} style={{ fontSize: 13, fontWeight: 600, color: BLANCO, ...PARTE_RENGLON,
                        ...(conNombres ? {} : { flex: css('1', '1', 'auto'), maxWidth: '100%' }) }}>{o.tema}</span>
                      {/* t7-3 remate 6 (B1, #575): la publicacion del tema, con el trato del principal (remate 5, C1): su
                          PROPIO renglon debajo del tema y su conteo, a todo el ancho, entero; con nombres, entre el tema y
                          los nombres. Dos temas con el mismo nombre ya no se ven identicos. */}
                      <span data-testid={`${id}-de`} title={o.card}
                        style={{ fontSize: 12, color: GRIS, ...PARTE_RENGLON,
                          ...(conNombres ? {} : { flexBasis: '100%', order: 3 }) }}>{o.card}</span>
                      {/* con 3 o menos, los nombres van debajo del tema (sin barra) y pueden partir renglon */}
                      <span data-testid={`${id}-cuenta`}
                        style={conNombres ? { fontSize: 12, color: GRIS }
                          // t7-3 remate 8 (#591, C61): el gris del tema principal (antes blanco)
                          : { fontSize: 13, color: GRIS, whiteSpace: 'nowrap', marginLeft: 'auto', order: 2 }}>
                        {/* t7-3 remate 7 (firma e): el conteo dice su verbo, como el del tema principal (antes "2 de 12") */}
                        {conNombres && o.le_cuesta_nombres
                          ? tt('groups.avance.nombres.le_cuesta', { count: o.le_cuesta_nombres.length, nombres: nombresDe(o.le_cuesta_nombres) })
                          : <ConNegritas texto={t('groups.avance.le_cuesta_a', { count: o.r, y })} />}
                      </span>
                    </div>
                    {!conNombres && <Barra dist={o.dist} alto={6} />}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  }
  return (
    <Respuesta testid="avance-col-temas" guia={ROJO} titulo={t('groups.avance.temas_titulo')}
      ayuda={t('groups.avance.ayuda_temas')}>
      <div data-testid="avance-cascada" data-estado={estado}>
        {datos.nadie_practico_esta_semana && estado !== 'sin_datos' && (
          <p data-testid="avance-nadie-semana" style={{ fontSize: 12, color: GRIS, marginBottom: 8 }}>{t('groups.avance.nadie_esta_semana')}</p>
        )}
        {cuerpo}
      </div>
      <PieNaranja testid="avance-ver-todos-temas" onClick={verPorCard}>{t('groups.avance.ver_todos_los_temas')}</PieNaranja>
    </Respuesta>
  );
}

/* ======================================================================
   3. "VA MEJORANDO?" (#544)
   ====================================================================== */

/** Un renglon de "Va mejorando?": rotulo, barra de conteo y "x de y"; con cero, "rotulo: nadie" y
    sin barra. Las piezas son celdas de la rejilla del grupo (asi las barras se alinean). */
function FilaDeConteo({ t, testid, rotulo, n, y, color, pasado }) {
  // t7-3 remate 7 (#582 A): la pista es el gris nuevo; la barra de la semana anterior, el color de hoy al 75 % sobre
  // la tarjeta (antes opacidad 0.5 sobre la pista: no se veia); y con "nadie" el renglon conserva su rotulo, su
  // conteo y su barra, entera en el gris nuevo (antes "rotulo: nadie", sin barra)
  return (
    <div data-testid={testid} style={{ display: 'contents' }}>
      <span data-testid={`${testid}-rotulo`} style={{ fontSize: 12, color: GRIS, whiteSpace: 'nowrap' }}>{rotulo}</span>
      <span data-testid={`${testid}-barra`}
        style={{ display: 'block', height: 10, background: GRIS_DE_LAS_BARRAS, borderRadius: 5, overflow: 'hidden', minWidth: 40 }}>
        <span style={{ display: 'block', height: '100%', width: anchoDeConteo(n, y),
          background: pasado ? atenuado(color, CAPA.background, 0.75) : color }} />
      </span>
      <span data-testid={`${testid}-numero`} style={{ fontSize: 14, color: BLANCO, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <strong style={{ fontWeight: 700 }}>{n ? t('groups.avance.x_de_y', { x: n, y }) : t('groups.avance.nadie')}</strong>
      </span>
    </div>
  );
}

/** t7-3 remate 8 (#591, C06): los dos grupos de "Va mejorando?" viven en UNA rejilla (`RejillaDeConteos`): su titulo
    ocupa el renglon entero y sus filas comparten la columna del rotulo, asi las cuatro barras arrancan en la misma
    vertical (antes cada grupo tenia su rejilla y "Practicaron" arrancaba 6 px mas a la derecha). */
function GrupoDeConteos({ testid, titulo, children, primero = false }) {
  return (
    <>
      <p data-testid={testid}
        style={{ fontSize: 13, fontWeight: 600, color: BLANCO, gridColumn: css('1', '/', '-1'), marginTop: primero ? 0 : 8 }}>
        {titulo}
      </p>
      {children}
    </>
  );
}

function RejillaDeConteos({ children }) {
  return (
    <div data-testid="avance-mejora-rejilla" style={{ display: 'grid', gridTemplateColumns: css('max-content', 'minmax(40px,1fr)', 'max-content'),
      alignItems: 'center', columnGap: 8, rowGap: 6 }}>
      {children}
    </div>
  );
}

function textoMejoro(tt, x) {
  return (x.temas || []).map((tm) => tt(tm.a === 'amarillo' ? 'groups.avance.mejoro_amarillo' : 'groups.avance.mejoro_verde',
    { tema: tm.tema })).join('. ');
}

function ColMejora({ t, tt, datos, todos, verTodos }) {
  const y = datos.grupo.alumnos;
  const m = datos.mejora || {};
  const conNombres = datos.con_nombres === true && m.nombres;
  let cuerpo;
  if (m.grupo_sin_practica_10_dias || datos.grupo_sin_practica_10_dias) {
    cuerpo = <Vacio testid="avance-mejora-nadie-10">{t('groups.avance.nadie_10')}</Vacio>;
  } else if (m.sin_semana_anterior || !m.practicaron) {
    cuerpo = <Vacio testid="avance-mejora-sin-semana">{t('groups.avance.sin_semana')}</Vacio>;
  } else if (conNombres) {
    const frase = (clave) => (
      <p key={clave} data-testid={`avance-mejora-nombres-${clave}`} style={{ fontSize: 13, color: GRIS }}>
        {/* t7-3 remate 7 (regla 5 de #581): con UNA persona, la hermana en singular ("Practico", "Mejoro") */}
        {tt(`groups.avance.nombres.${clave}`, { nombres: listaDeNombres(t, m.nombres[clave]), count: m.nombres[clave].length })}
      </p>
    );
    cuerpo = (
      <div style={{ display: 'grid', gap: 6 }}>
        {frase('practicaron_hoy')}
        {frase('practicaron_antes')}
        {frase('en_rojo_hoy')}
        {frase('en_rojo_antes')}
        {(m.nombres.mejoraron || []).length > 0 && (
          <div data-testid="avance-mejoraron" style={{ marginTop: 6 }}>
            {frase('mejoraron')}
            <p data-testid="avance-mejoraron-reconocelos" style={{ fontSize: 12, color: GRIS, marginTop: 4 }}>
              {/* t7-3 remate 8 (#591, texto 1): con UN alumno, "Reconoce su avance en clase." */}
              {t(m.nombres.mejoraron.length === 1 ? 'groups.avance.reconocelos_one' : 'groups.avance.reconocelos')}
            </p>
          </div>
        )}
      </div>
    );
  } else {
    const alumnos = m.mejoraron_alumnos || [];
    const visibles = todos ? alumnos : alumnos.slice(0, RENGLONES_MEJORARON);
    const n = m.mejoraron_n ?? alumnos.length;
    cuerpo = (
      <div style={{ display: 'grid', gap: 14 }}>
        <RejillaDeConteos>
        <GrupoDeConteos testid="avance-mejora-grupo-practicaron" titulo={t('groups.avance.grupo_practicaron')} primero>
          <FilaDeConteo t={t} testid="avance-mejora-esta-semana" rotulo={t('groups.avance.mejora_fila.esta_semana')}
            n={m.practicaron.hoy} y={y} color={NARANJA} />
          <FilaDeConteo t={t} testid="avance-mejora-semana-anterior" rotulo={t('groups.avance.mejora_fila.semana_anterior')}
            n={m.practicaron.antes} y={y} color={NARANJA} pasado />
        </GrupoDeConteos>
        <GrupoDeConteos testid="avance-mejora-grupo-rojo" titulo={t('groups.avance.kpi_rojo')}>
          <FilaDeConteo t={t} testid="avance-mejora-hoy" rotulo={t('groups.avance.mejora_fila.hoy')}
            n={m.en_rojo.hoy} y={y} color={ROJO} />
          <FilaDeConteo t={t} testid="avance-mejora-hace-una-semana" rotulo={t('groups.avance.mejora_fila.hace_una_semana')}
            n={m.en_rojo.antes} y={y} color={ROJO} pasado />
        </GrupoDeConteos>
        </RejillaDeConteos>
        {n > 0 && (
          <div data-testid="avance-mejoraron" style={{ ...TARJETA, ...relleno(12, 14) }}>
            <p data-testid="avance-mejoraron-titulo" style={{ fontSize: 13, fontWeight: 600, color: BLANCO }}>
              <ConNegritas texto={t('groups.avance.mejoraron_de', { count: n, y })} />
            </p>
            <ul style={{ display: 'grid', gap: 6, marginTop: 8 }}>
              {visibles.map((x, i) => (
                <li key={x.alumno_id} data-testid={`avance-mejoro-${i}`}
                  className="flex items-center" style={{ gap: 8, fontSize: 13, color: BLANCO, minWidth: 0 }}>
                  <Iniciales texto={x.iniciales} lado={28} />
                  <span style={PARTE_RENGLON} title={`${x.nombre}: ${textoMejoro(tt, x)}`}>
                    {x.nombre}
                    <span style={{ color: GRIS }}>{`: ${textoMejoro(tt, x)}`}</span>
                  </span>
                </li>
              ))}
            </ul>
            {!todos && alumnos.length > RENGLONES_MEJORARON && (
              <PieNaranja testid="avance-mejoraron-ver-todos" onClick={verTodos} arriba={8}>{t('groups.avance.ver_todos')}</PieNaranja>
            )}
            <p data-testid="avance-mejoraron-reconocelos" style={{ fontSize: 12, color: GRIS, marginTop: 8 }}>
              {t(n === 1 ? 'groups.avance.reconocelos_one' : 'groups.avance.reconocelos')}
            </p>
          </div>
        )}
      </div>
    );
  }
  return (
    <Respuesta testid="avance-col-mejora" guia={VERDE} titulo={t('groups.avance.mejora_titulo')}
      ayuda={t('groups.avance.ayuda_mejora')}>
      {/* LAPIDA t7-3 remate 2 (R7): murio la nota "Hoy contra hace una semana"; su texto sigue en la ayuda del "?". */}
      {cuerpo}
    </Respuesta>
  );
}

/* ======================================================================
   LOS AVISOS DE CARD (#545): debajo de las tres respuestas
   ====================================================================== */

/** t7-3 remate 8 (#591, C49): el nombre de la publicacion en negritas dentro de su aviso (una publicacion sin nombre
    propio es una lista de temas y se fundia con la frase); el numero de dias, como antes. */
function ConElNombre({ texto, nombre, n }) {
  const i = nombre ? texto.indexOf(nombre) : -1;
  if (i < 0) return n > 1 ? <NumeroEnNegritas texto={texto} n={n} /> : texto;
  const resto = texto.slice(i + nombre.length);
  return (
    <>
      {texto.slice(0, i)}
      <strong data-testid="avance-aviso-nombre" style={{ color: BLANCO, fontWeight: 700 }}>{nombre}</strong>
      {n > 1 ? <NumeroEnNegritas texto={resto} n={n} /> : resto}
    </>
  );
}

function AvisosDeCard({ t, tt, lang, datos, abrirCard, apilada }) {
  const avisos = datos.avisos_cards || [];
  if (!avisos.length) return null;
  const conNombres = datos.con_nombres === true;
  return (
    <div data-testid="avance-avisos"
      style={{ display: 'grid', gridTemplateColumns: apilada || avisos.length === 1 ? '1fr' : css('1fr', '1fr'), gap: 10, marginTop: 14 }}>
      {avisos.map((a) => {
        const id = `avance-aviso-${a.publicacion_id}`;
        const caduca = a.dias <= 0
          ? tt('groups.avance.aviso.caduca_hoy', { card: a.nombre })
          : tt('groups.avance.aviso.caduca_en', { count: a.dias, card: a.nombre });
        let practicaron;
        if (conNombres && a.nombres) {
          // t7-3 remate 8 (#591, C18): "A y B" / "A and B" (antes "A, B")
          practicaron = a.nombres.length
            ? tt('groups.avance.nombres.aviso', { count: a.nombres.length, nombres: unirNombres(a.nombres, lang) })
            : t('groups.avance.aviso.nadie');
        } else {
          practicaron = a.x ? t('groups.avance.aviso.practicaron', { count: a.x, y: a.y }) : t('groups.avance.aviso.nadie');
        }
        return (
          <div key={id} data-testid={id} {...botonDe(() => abrirCard(a.publicacion_id))} aria-expanded={undefined}
            className="border border-[#262626] hover:border-[#3a3a3a] transition-colors"
            style={{ background: '#181818', borderRadius: 12, ...relleno(12, 14), cursor: 'pointer', display: 'flex', gap: 10 }}>
            <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: ROJO, flexShrink: 0, marginTop: 5 }} />
            <div style={{ minWidth: 0 }}>
              <p data-testid={`${id}-caduca`} style={{ fontSize: 14, color: BLANCO, overflowWrap: 'anywhere' }}>
                <ConElNombre texto={caduca} nombre={a.nombre} n={a.dias} />
              </p>
              <p data-testid={`${id}-practicaron`} style={{ fontSize: 13, color: GRIS, marginTop: 2, overflowWrap: 'anywhere' }}>
                <ConNegritas texto={practicaron} />
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ======================================================================
   POR CARD (mosaicos)
   ====================================================================== */

/** t7-3 remate 8 (#591, C63): el ancho comun de la columna de numeros de "Tema por tema" (cinco numeros de dos cifras
    con su cuadrito: 5 x (9 + 4 + 16) + 4 x 10 = 185 como mucho). */
export const ANCHO_DE_LOS_NUMEROS = 185;

function FalladaDeLaCard({ t, tt, f }) {
  const testid = `avance-fallada-${f.question_id}`;
  const eligen = renglonesEligen(tt, f);
  return (
    <div data-fallada="si" data-testid={testid} style={{ ...CAJA, ...relleno(10, 12), display: 'grid', gap: 4, fontSize: 13 }}>
      <p data-testid={`${testid}-rotulo`}
        style={{ color: NARANJA, fontWeight: 700, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', ...PARTE_RENGLON }}
        title={f.tema}>
        {f.dificultad ? `${f.tema} (${nombreDificultad(t, f.dificultad)})` : f.tema}
      </p>
      <p style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, color: BLANCO, ...PARTE_RENGLON }}>{`"${f.texto}"`}</p>
      <p>
        <span data-testid={`${testid}-chip`}
          style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, borderRadius: 999, ...relleno(2, 9),
            background: ROJO, color: LETRA_SOBRE_EL_ROJO }}>
          {/* t7-2d remate (#551): con m = 1, "La fallo el unico que la contesto." (nunca "de los 1"). t7-3 remate 8
              (#591, C84): la frase completa, con mayuscula y punto, la misma de la caja de la portada */}
          {textoFallan(t, f.n, f.m)}
        </span>
      </p>
      {/* t7-3 remate 7 (#582 D): la equivocada dicha como tal, "Por que esta mal" y al final la correcta */}
      {eligen.map((x, i) => <p key={i} style={{ color: GRIS }}><ConRotulo texto={x} tono="equivocada" /></p>)}
      {/* t7-3 remate 7 (hermanos): "Por que esta mal:" con su rotulo en negritas, como en la portada */}
      {f.por_que && <p style={{ color: GRIS }}><ConRotulo testid={`${testid}-por-que`} texto={tt('groups.avance.por_que', { razon: f.por_que })} /></p>}
      {f.correcta && <p style={{ color: GRIS }}><ConRotulo texto={textoCorrecta(tt, f)} tono="correcta" /></p>}
    </div>
  );
}

function PorCard({ t, tt, lang, datos, abierta, alternar, angosto }) {
  const y = datos.grupo.alumnos;
  return (
    <div className="space-y-3">
      {(datos.cards || []).map((c) => {
        const cid = c.publicacion_id;
        const esta = abierta === cid;
        const chip = chipDeCaduca(c);
        const meta = [t('groups.avance.publicada', { fecha: formatearFechaDeCaducidad(c.publicada_en, lang) }),
          t('doc_card.topic_count', { count: c.temas_n }), t('groups.avance.preguntas', { count: c.preguntas_n })].join(', ');
        const caras = c.practicaron_iniciales || [];
        const mas = Math.max(0, (c.practicaron || 0) - caras.length);
        return (
          // t7-3 remate 2 (R6, #566): cada publicacion se ve PLEGABLE, como el renglon de riesgo: la cabecera
          // entera es UN boton (aria-expanded, aria-controls, 44 px de alto minimo) con la flecha que gira al
          // abrir; el recuadro, borde naranja al pasar el raton y abierto; un clic en zona vacia tambien alterna.
          <div key={cid} id={`avance-mosaico-${cid}`} data-testid={`avance-mosaico-${cid}`}
            onClick={(e) => {
              const boton = e.target && typeof e.target.closest === 'function' ? e.target.closest('button') : null;
              if (boton && e.currentTarget.contains(boton)) return;
              alternar(cid);
            }}
            className={`border ${esta ? 'border-[#FF6B00]' : 'border-[#262626] hoverable:border-[#FF6B00]'} transition-colors`}
            style={{ background: '#181818', borderRadius: 14, padding: 16, cursor: 'pointer',
              ...(c.caducada ? { opacity: 0.75 } : {}) }}>
            <button type="button" data-testid={`avance-mosaico-${cid}-cabecera`} aria-expanded={esta}
              aria-controls={`avance-mosaico-${cid}-detalle`} onClick={soloEste(() => alternar(cid))}
              style={{ display: 'block', width: '100%', minHeight: 44, padding: 0, background: 'transparent', textAlign: 'left',
                cursor: 'pointer', color: 'inherit' }}>
            <span style={angosto ? { display: 'grid', gap: 8 }
              : { display: 'grid', gridTemplateColumns: css('minmax(0,1fr)', 'auto'), gap: css('4px', '16px'), alignItems: 'center' }}>
              <span style={{ display: 'block', minWidth: 0 }}>
                <span className="flex flex-wrap items-center" style={{ gap: 10 }}>
                  {c.caducada && <Lock className="w-4 h-4 shrink-0 text-[#a0a0a0]" aria-hidden="true" />}
                  <span data-testid={`avance-mosaico-${cid}-nombre`} title={c.nombre}
                    style={{ fontSize: 18, fontWeight: 700, color: BLANCO, ...PARTE_RENGLON }}>{c.nombre}</span>
                  {chip && (
                    <span data-testid={`avance-mosaico-${cid}-chip`}
                      style={{ fontSize: 11, fontWeight: 600, borderRadius: 999, ...relleno(2, 9), whiteSpace: 'nowrap',
                        ...(chip.rojo ? { background: ROJO, color: LETRA_SOBRE_EL_ROJO, ...borde(ROJO) }
                          : { background: '#2a2a2a', color: BLANCO, ...borde(BORDE_SIN_PRACTICAR) }) }}>
                      {chip.llave === 'caduco_el'
                        ? tt('groups.avance.caduco_el', { fecha: fechaDeCaducidadDe(c, lang) })
                        : (chip.llave === 'caduca_hoy' ? t('groups.avance.caduca_hoy') : t('groups.avance.caduca_en', { count: chip.count }))}
                    </span>
                  )}
                </span>
                <span data-testid={`avance-mosaico-${cid}-meta`} style={{ display: 'block', fontSize: 12, color: GRIS, marginTop: 2 }}>
                  {meta}
                </span>
              </span>
              {/* post-t7-1 (A5): las caras, "x de y han practicado" y la flecha parten renglon cuando no caben (a 320 se
                  salian del borde); cada pieza sigue entera. Con todo en un renglon se ve igual que antes. */}
              <span className="flex flex-wrap items-center" style={{ columnGap: 10, rowGap: 4, fontSize: 14, color: GRIS }}>
                {/* t7-3 remate 7 (regla 2 de #581): sin caras no se pinta la tira (el "Nadie" se corria 10 px) */}
                {(caras.length > 0 || mas > 0) && (
                <span className="inline-flex" data-testid={`avance-mosaico-${cid}-caras`}>
                  {caras.map((x, i) => (
                    <span key={`${x}-${i}`} className="inline-flex items-center justify-center"
                      style={{ width: 26, height: 26, borderRadius: 999, background: '#222222', ...borde('#181818', 2),
                        marginLeft: i ? -SOLAPE_DE_LAS_CARAS : 0, fontSize: 10, fontWeight: 700, color: GRIS }}>{x}</span>
                  ))}
                  {mas > 0 && (
                    <span className="inline-flex items-center justify-center"
                      style={{ width: 26, height: 26, borderRadius: 999, background: '#222222', ...borde('#181818', 2),
                        marginLeft: -SOLAPE_DE_LAS_CARAS, fontSize: 10, fontWeight: 700, color: GRIS }}>{`+${mas}`}</span>
                  )}
                </span>
                )}
                <span data-testid={`avance-mosaico-${cid}-practicaron`} style={{ whiteSpace: 'nowrap' }}>
                  {c.practicaron
                    ? <ConNegritas texto={t('groups.avance.practicaron_de', { count: c.practicaron, y })} />
                    : t('groups.avance.practicaron_nadie')}
                </span>
                <ChevronDown aria-hidden="true" data-testid={`avance-mosaico-${cid}-flecha`}
                  className={`w-4 h-4 text-[#8c8c8c] shrink-0 transition-transform ${esta ? 'rotate-180' : ''}`} />
              </span>
            </span>
            </button>
            <div style={{ marginTop: 12 }}>
              <Barra dist={c.dist} alto={12} estados={ESTADOS_DE_LA_CARD} testid={`avance-mosaico-${cid}-barra`} />
              {/* la regla del cero: un color sin nadie no dice "Dominan 0". t7-3 remate 3 (#569): seis estados;
                  "Va bien" y "Ha practicado poco" con su texto de publicacion ("Van bien 2", "Han practicado poco 7") */}
              <div className="flex flex-wrap items-center" data-testid={`avance-mosaico-${cid}-leyenda`}
                style={{ gap: css('6px', '12px'), marginTop: 8, fontSize: 12, color: GRIS }}>
                {ESTADOS_DE_LA_CARD.filter((e) => (c.dist?.[e] || 0) > 0).map((e) => (
                  <span key={e} className="inline-flex items-center" style={{ gap: 5 }}>
                    <Cuadrito estado={e} />
                    {t(e === 'va_bien' || e === 'poco' ? `groups.avance.conteo_pub.${e}` : `groups.avance.conteo.${e}`,
                      { count: c.dist[e] })}
                  </span>
                ))}
              </div>
            </div>
            {esta && (
              <div id={`avance-mosaico-${cid}-detalle`} data-testid={`avance-mosaico-${cid}-detalle`}
                onClick={(e) => e.stopPropagation()} role="presentation"
                style={{ cursor: 'auto', marginTop: 16 }}>
                <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: GRIS, fontWeight: 700 }}>
                  {t('groups.avance.tema_por_tema')}
                </p>
                <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
                  {c.temas.map((tm) => (
                    <div key={tm.tema_id} data-testid={`avance-tema-fila-${cid}--${tm.tema_id}`}
                      style={angosto ? { display: 'grid', gap: 6 }
                        // t7-3 remate 8 (#591, C47 y C63): la columna de los numeros con ancho comun: todas las barras de una
                        // publicacion miden lo mismo
                        : { display: 'grid', gridTemplateColumns: css('200px', 'minmax(0,1fr)', `${ANCHO_DE_LOS_NUMEROS}px`), gap: 12,
                          alignItems: 'center' }}>
                      <span title={tm.nombre} style={{ fontSize: 14, fontWeight: 600, color: BLANCO, ...PARTE_RENGLON }}>{tm.nombre}</span>
                      <Barra dist={tm.dist} alto={8} />
                      {/* cada numero de color con su rotulo al pasar el raton; un color sin nadie no se escribe */}
                      <span data-testid={`avance-tema-fila-${cid}--${tm.tema_id}-cuentas`}
                        aria-label={ESTADOS.filter((e) => tm.dist[e]).map((e) => t(`groups.avance.conteo.${e}`, { count: tm.dist[e] })).join(', ')}
                        className="flex" style={{ gap: 10, fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                          justifyContent: angosto ? 'flex-start' : 'flex-end' }}>
                        {/* t7-3 remate 8 (#591, C31 y C132): cada numero lleva delante el cuadrito de su color (el de "Por
                            alumno"), para que se entienda sin pasar el raton */}
                        {ESTADOS.filter((e) => tm.dist[e]).map((e) => (
                          <span key={e} data-estado={e} title={t(`groups.avance.estado.${e}`)} className="inline-flex items-center"
                            style={{ gap: 4, color: e === 'S' ? GRIS_DE_LAS_BARRAS : (e === 'va_bien' ? VERDE : COLOR_DEL_ESTADO[e]),
                              ...(e === 'va_bien' ? { opacity: 0.7 } : {}) }}>
                            <Cuadrito estado={e} lado={9} />{tm.dist[e]}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
                {(c.preguntas_mas_falladas || []).length > 0 && (
                  <div style={{ marginTop: 16, borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: '#262626', paddingTop: 14 }}>
                    <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: GRIS, fontWeight: 700,
                      marginBottom: 10 }}>
                      {t('groups.avance.mas_fallan')}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: angosto ? '1fr' : 'repeat(3,minmax(0,1fr))', gap: 10,
                      alignItems: 'start' }}>
                      {c.preguntas_mas_falladas.slice(0, 3).map((f) => <FalladaDeLaCard key={f.question_id} t={t} tt={tt} f={f} />)}
                    </div>
                  </div>
                )}
                {lineasDificultad(t, c.dificultad_card).length > 0 && (
                  <div style={{ marginTop: 10 }}>
                    <Pista color={AMARILLO} testid={`avance-mosaico-${cid}-dificultad`}>
                      <Renglones lineas={lineasDificultad(t, c.dificultad_card)} />
                    </Pista>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ======================================================================
   POR ALUMNO Y EL DETALLE (#547): por motivo, con buscador y "Recordar a todos ellos"
   ====================================================================== */

function BotonTodos({ t, clave, alumnos, recordados, enviandoTodos, recordarTodos, testid }) {
  const pendientes = alumnos.filter((a) => !(recordados[a.id] || a.recordado_hoy)).map((a) => a.id);
  const hecho = pendientes.length === 0;
  return (
    <button type="button" data-testid={testid || `avance-recordar-todos-${clave}`}
      // t7-3 remate 8 (revisor de flujos): apagado mientras va, como "Recordar" (C90)
      aria-disabled={hecho || enviandoTodos[clave] ? 'true' : undefined} aria-busy={enviandoTodos[clave] ? 'true' : undefined}
      onClick={() => { if (!hecho) recordarTodos(clave, pendientes); }}
      className="shrink-0 whitespace-nowrap rounded-full relative"
      style={{ height: 32, paddingLeft: 12, paddingRight: 12, fontSize: 12, fontWeight: 600, ...borde('#333333'),
        color: hecho ? GRIS : BLANCO, background: 'transparent', cursor: hecho ? 'default' : 'pointer',
        opacity: enviandoTodos[clave] ? 0.6 : 1 }}>
      <span aria-hidden="true" style={{ position: 'absolute', left: 0, right: 0, top: -7, bottom: -7 }} />
      {hecho ? t('groups.avance.recordado_hoy') : t('groups.avance.recordar_todos')}
    </button>
  );
}

const TOQUE_DEL_BUSCADOR = { display: 'block', paddingTop: 2, paddingBottom: 2, marginTop: -2, marginBottom: -2 };

function PorAlumno({ t, datos, abierto, alternar, recordados, enviando, recordar, angosto, plegados, alternarMotivo,
  busqueda, setBusqueda, recordarTodos, enviandoTodos }) {
  const y = datos.grupo.alumnos;
  const porId = Object.fromEntries((datos.alumnos || []).map((a) => [a.id, a]));
  const cardPorId = Object.fromEntries((datos.cards || []).map((c) => [c.publicacion_id, c]));
  const conMotivo = new Set();
  const secciones = (datos.riesgo_por_motivo || []).map((m) => {
    (m.alumnos || []).forEach((id) => conMotivo.add(id));
    return { clave: m.motivo, x: m.x, alumnos: (m.alumnos || []).map((id) => porId[id]).filter(Boolean) };
  });
  const sin = (datos.alumnos || []).filter((a) => !conMotivo.has(a.id)).sort((a, b) => a.nombre.localeCompare(b.nombre));
  if (sin.length) secciones.push({ clave: 'sin', x: sin.length, alumnos: sin });
  const filtro = sinAcentos(busqueda.trim());
  const pasa = (a) => !filtro || sinAcentos(a.nombre).includes(filtro);
  const lineaDe = (a) => (a.nunca ? [t('groups.avance.riesgo.nunca')]
    : [t('groups.avance.ultima_practica', { cuando: cuandoTexto(t, a.ultima_practica) }),
      t('groups.avance.contestadas', { count: a.preguntas_contestadas || 0 })]);
  // t7-3 (#556, regla 22): cada pieza es su propia frase: "Ultima practica: ayer. 12 preguntas contestadas."
  const enFrases = (piezas) => (piezas.length > 1 ? `${piezas.join('. ')}.` : piezas.join(''));

  const fila = (a) => {
    const esta = abierto === a.id;
    return (
      <div key={a.id} id={`avance-alumno-${a.id}`} data-testid={`avance-alumno-${a.id}`} {...botonDe(() => alternar(a.id), esta)}
        className="border border-[#262626] hover:border-[#3a3a3a] transition-colors"
        style={{ background: '#181818', borderRadius: 12, ...relleno(12, 16), cursor: 'pointer' }}>
        <div style={angosto ? { display: 'grid', gap: 8 } : { display: 'flex', alignItems: 'center', gap: 16 }}>
          <div className="flex items-center" style={{ gap: 12, minWidth: 0, flex: angosto ? undefined : 1 }}>
            <Iniciales texto={a.iniciales} />
            <div style={{ minWidth: 0 }}>
              <p title={a.nombre} style={{ fontSize: 15, fontWeight: 600, color: BLANCO, ...PARTE_RENGLON }}>{a.nombre}</p>
              <p style={{ fontSize: 12, color: GRIS }} data-testid={`avance-alumno-${a.id}-linea`}>{enFrases(lineaDe(a))}</p>
              {/* t7-3 remate 2 (R7): "Practico en {{s}} dias distintos" en su renglon, en la UNICA cabecera */}
              {!a.nunca && (
                <p style={{ fontSize: 12, color: GRIS }} data-testid={`avance-alumno-${a.id}-dias`}>
                  {t('groups.avance.practico_dias', { count: a.dias_distintos ?? a.dias_practicados ?? 0 })}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 12, flexShrink: 0 }}>
            <div style={{ width: angosto ? undefined : 220, flex: angosto ? 1 : undefined, minWidth: 0 }}>
              <Barra dist={a.resumen_temas} testid={`avance-alumno-${a.id}-barra`} />
              <p className="flex flex-wrap items-center" style={{ gap: css('2px', '8px'), fontSize: 12, color: GRIS, marginTop: 4 }}
                data-testid={`avance-alumno-${a.id}-conteos`}>
                {/* t7-3 remate 7 (regla 5 de #581): un estado sin temas no se escribe (como en "Tema por tema") */}
                {ESTADOS.filter((e) => a.resumen_temas[e]).map((e) => (
                  <span key={e} className="inline-flex items-center" style={{ gap: 4 }} title={t(`groups.avance.estado.${e}`)}>
                    <Cuadrito estado={e} lado={9} />{a.resumen_temas[e]}
                  </span>
                ))}
              </p>
            </div>
            <BotonRecordar t={t} alumno={a} recordados={recordados} enviando={enviando} recordar={recordar}
              testid={`avance-recordar-${a.id}`} />
          </div>
        </div>
        {esta && (
          <div data-testid={`avance-alumno-${a.id}-detalle`} onClick={(e) => e.stopPropagation()} role="presentation"
            style={{ cursor: 'auto', marginTop: 14, borderTopWidth: 1, borderTopStyle: 'solid', borderTopColor: '#262626',
              paddingTop: 14 }}>
            {/* LAPIDA t7-3 remate 2 (R7): murio la cabecera repetida del detalle (el nombre otra vez con "Ultima
                practica..."); el detalle abre debajo con las publicaciones. */}
            <div style={{ display: 'grid', gridTemplateColumns: angosto ? '1fr' : 'repeat(2,minmax(0,1fr))', gap: 8 }}>
              {a.cards.map((ca) => {
                const c = cardPorId[ca.publicacion_id] || { temas: [] };
                const nombres = Object.fromEntries((c.temas || []).map((tm) => [tm.tema_id, tm.nombre]));
                // LAPIDA t7-3 remate 3 (#569): murio el renglon "Preparacion n de 100, q preguntas contestadas" de
                // cada publicacion: en su lugar, la barrita y sus conteos (con algo contestado). "caducada" se queda.
                const idCaja = `avance-alumno-${a.id}-card-${ca.publicacion_id}`;
                return (
                  <div key={ca.publicacion_id} data-testid={idCaja} style={{ ...CAJA, padding: 10 }}>
                    {/* la etiqueta baja a su propio renglon si no cabe junto al nombre (nunca se corta) */}
                    <div className="flex flex-wrap justify-between" style={{ gap: css('6px', '8px'), alignItems: 'flex-start' }}>
                      <span title={c.nombre} className="flex items-center"
                        style={{ fontSize: 13, fontWeight: 600, color: BLANCO, gap: 6, minWidth: 0, flex: css(1, 1, '160px') }}>
                        {c.caducada && <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />}
                        <span style={PARTE_RENGLON}>{c.nombre}</span>
                      </span>
                      <span data-estado={ca.estado} data-testid={`${idCaja}-estado`} title={t(`groups.avance.estado.${ca.estado}`)}
                        style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, ...relleno(2, 8), whiteSpace: 'nowrap',
                          background: COLOR_DEL_ESTADO[ca.estado], color: TEXTO_SOBRE_EL_ESTADO[ca.estado],
                          ...(ca.estado === 'S' ? borde(GRIS_DE_LAS_BARRAS) : {}),
                          ...(ca.estado === 'va_bien' ? { ...borde(VERDE, 2), ...relleno(0, 7) } : {}),
                          ...(ca.estado === 'poco' ? { ...borde(BORDE_POCO, 2), ...relleno(0, 7) } : {}) }}>
                        {t(`groups.avance.estado.${ca.estado}`)}
                      </span>
                    </div>
                    {c.caducada && (
                      <p data-testid={`${idCaja}-linea`} style={{ fontSize: 11, color: GRIS, marginTop: 2 }}>
                        {t('groups.avance.caducada')}
                      </p>
                    )}
                    {/* t7-3 remate 7 (pasada 2, regla 7): un servidor anterior no manda los conteos: sin ellos no se pinta
                        (antes salia "De 0 preguntas: 0 bien, 0 mal, 0 sin contestar."). t7-3 remate 8 (#588): una
                        publicacion sin practicar pinta solo "No ha contestado ninguna de las y preguntas.", sin barrita */}
                    {ca.total != null && (ca.estado === 'S'
                      ? <RenglonesDeConteos t={t} ca={ca} idCaja={idCaja} />
                      : <BarritaDePreguntas t={t} ca={ca} idCaja={idCaja} />)}
                    <ul style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                      {ca.temas.map((tm) => (
                        <li key={tm.tema_id} data-testid={`avance-alumno-${a.id}-tema-${ca.publicacion_id}-${tm.tema_id}`}
                          // post-t7-1 (A5): el nombre del tema mide al menos su palabra mas larga (no se parte a la mitad); el
                          // conteo de la derecha cede y parte entre palabras. Con lugar de sobra, igual que antes.
                          style={{ display: 'grid', gridTemplateColumns: css('14px', 'minmax(min-content,1fr)', 'auto'), gap: 8, alignItems: 'start', fontSize: 12,
                            color: GRIS }}>
                          <span style={{ paddingTop: 3, lineHeight: 0 }}>
                            <Cuadrito estado={tm.estado} titulo={t(`groups.avance.estado.${tm.estado}`)} />
                          </span>
                          <span title={nombres[tm.tema_id] || ''} style={PARTE_RENGLON}>{nombres[tm.tema_id] || ''}</span>
                          {tm.estado !== 'S' ? (
                            // post-t7-1 (A5): a 320 su "nowrap" le quitaba lugar al nombre del tema y lo partia a la mitad
                            // ("Derivada / s"); ahora parte entre palabras (el conteo "x de y" nunca), y el nombre no se parte
                            <span data-testid={`avance-alumno-${a.id}-tema-${ca.publicacion_id}-${tm.tema_id}-acierta`}
                              style={{ color: GRIS, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
                              {/* t7-3 remate 7 (regla 5 de #581): con cero aciertos, la forma del cero */}
                              {sinPartirElConteo(tm.acierta ? t('groups.avance.acierta', { a: tm.acierta, b: tm.contestadas })
                                : t('groups.avance.acierta_ninguna', { b: tm.contestadas }))}
                            </span>
                          ) : <span />}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
            {/* t7-3 remate 8 (#588): UNA vez por alumno, al final, en gris chico, solo si contesto algo */}
            {a.cards.some((ca) => (ca.bien || 0) + (ca.mal || 0) > 0) && (
              <p data-testid={`avance-alumno-${a.id}-nota`} style={{ fontSize: 12, color: GRIS, marginTop: 10 }}>
                {t('groups.avance.preguntas_pub.nota')}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* t7-3 remate 7 (pasada 4, regla 2 de #581): 44 de toque sin mover el campo de 40 (la etiqueta lleva 2 px de
          relleno con margen negativo; un toque en ella enfoca el campo) */}
      {y > BUSCADOR_DESDE && (
        <label style={TOQUE_DEL_BUSCADOR}>
          <input type="search" data-testid="avance-buscar" value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
            placeholder={t('groups.avance.buscar_por_nombre')} aria-label={t('groups.avance.buscar_por_nombre')}
            style={{ width: '100%', height: 40, ...relleno(0, 12), borderRadius: 10, ...borde('#262626'), background: '#111111',
              color: BLANCO, fontSize: 14 }} />
        </label>
      )}
      {/* t7-3 remate 7 (regla 7 de #581): sin coincidencias, el aviso que ya existe (antes la seccion quedaba en blanco) */}
      {filtro && secciones.every((s) => !s.alumnos.some(pasa)) && (
        <Vacio testid="avance-buscar-nada">{t('tarjeta_plegable.nada_coincide')}</Vacio>
      )}
      {secciones.map((s) => {
        const visibles = s.alumnos.filter(pasa);
        if (filtro && !visibles.length) return null;
        const abierta = filtro ? true : !plegados[s.clave];
        const id = `avance-seccion-motivo-${s.clave}`;
        return (
          <section key={s.clave} id={`avance-motivo-${s.clave}`} data-testid={id} data-motivo={s.clave} className="space-y-2">
            <div className="flex items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
              <button type="button" data-testid={`${id}-plegar`} aria-expanded={abierta} onClick={() => alternarMotivo(s.clave)}
                className="inline-flex items-center"
                style={{ gap: 8, minHeight: 32, flex: 1, minWidth: 'max-content', background: 'transparent', textAlign: 'left',
                  flexWrap: 'nowrap', position: 'relative' }}>
                <AreaDeToque />
                <ChevronDown aria-hidden="true"
                  className={`w-4 h-4 text-[#8c8c8c] shrink-0 transition-transform ${abierta ? 'rotate-180' : ''}`} />
                {s.clave === 'sin' ? (
                  <span data-testid={`${id}-titulo`} style={{ fontSize: 14, fontWeight: 700, color: BLANCO }}>
                    <ConNegritas texto={t('groups.avance.sin_motivo', { x: s.x, y })} />
                  </span>
                ) : (
                  <>
                    <ChipDeMotivo motivo={s.clave} testid={`${id}-chip`}>{t(`groups.avance.motivo.${s.clave}`, { count: s.x })}</ChipDeMotivo>
                    <span data-testid={`${id}-cuenta`} style={{ fontSize: 14, color: BLANCO, whiteSpace: 'nowrap' }}>
                      <strong style={{ fontWeight: 700 }}>{t('groups.avance.x_de_y', { x: s.x, y })}</strong>
                    </span>
                  </>
                )}
              </button>
              {/* t7-3 remate 8 (#591, decision 13): con el buscador filtrando, "Recordar a todos ellos" manda solo a los de
                  esta seccion que se ven, y solo aparece si se ven dos o mas; sin filtro, como siempre */}
              {s.clave !== 'sin' && (filtro ? visibles : s.alumnos).length >= 2 && (
                <BotonTodos t={t} clave={s.clave} alumnos={filtro ? visibles : s.alumnos} recordados={recordados}
                  enviandoTodos={enviandoTodos} recordarTodos={recordarTodos} />
              )}
            </div>
            {abierta && visibles.map(fila)}
          </section>
        );
      })}
    </div>
  );
}

/** t7-3 remate 3 (#569, C3): la barrita de una publicacion para un alumno, en lugar de la Preparacion: a todo el
    ancho, 8 px, verde lo que contesta bien, rojo lo que contesta mal y gris lo que todavia no contesta (el ancho de
    cada tramo es su conteo entre el total; uno con algo nunca mide menos de 3 px; uno en cero no se pinta). RE-ANCLADA
    EN t7-3 remate 8 (#588): debajo, los dos renglones ("Ha contestado 7 de 62 preguntas." y "De esas 7, le salen bien 3
    y mal 4."), cada numero en blanco y negritas; su `aria-label` son esas frases. RE-ANCLADA EN t7-3 remate 8 (ensayo,
    DIF-2): cada uno en SU renglon, como firmo Carlos (antes iban corridos y a 390 partian a media frase). LAPIDA: murieron
    el renglon "De 62 preguntas: 27 bien, 2 mal, 33 sin contestar" y sus puntos de color. */
const COLOR_DEL_TRAMO = { bien: VERDE, mal: ROJO, falta: RIEL };

/** Un renglon con cada numero en blanco y negritas. */
function ConNumerosEnNegritas({ texto }) {
  return texto.split(/(\d+)/).map((p, i) => (/^\d+$/.test(p)
    ? <strong key={i} style={{ color: BLANCO, fontWeight: 700 }}>{p}</strong>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

function RenglonesDeConteos({ t, ca, idCaja }) {
  const lineas = lineasDeConteos(t, ca.total || 0, ca.bien || 0, ca.mal || 0);
  return (
    <p data-testid={`${idCaja}-conteos`} style={{ fontSize: 12, lineHeight: 1.5, color: GRIS, marginTop: 6 }}>
      {/* el espacio entre los dos renglones se queda como texto (lo lee el lector de pantalla); en pantalla no se ve */}
      {lineas.map((l, i) => (
        <React.Fragment key={i}>{i ? ' ' : ''}<span data-renglon="si" style={{ display: 'block' }}>
          <ConNumerosEnNegritas texto={l} /></span></React.Fragment>
      ))}
    </p>
  );
}

function BarritaDePreguntas({ t, ca, idCaja }) {
  const total = ca.total || 0;
  const lineas = lineasDeConteos(t, total, ca.bien || 0, ca.mal || 0);
  return (
    <div style={{ marginTop: 8 }}>
      <div role="img" aria-label={lineas.join(' ')} data-testid={`${idCaja}-barrita`} className="flex overflow-hidden w-full"
        style={{ height: 8, borderRadius: 4, background: RIEL }}>
        {tramosDeLaBarrita(ca.bien || 0, ca.mal || 0, total).map(({ clave, n }) => (
          <span key={clave} data-tramo={clave}
            style={{ flexGrow: n, flexBasis: 0, minWidth: 3, background: COLOR_DEL_TRAMO[clave],
              ...(clave === 'falta' ? borde(GRIS_DE_LAS_BARRAS) : {}), boxSizing: 'border-box' }} />
        ))}
      </div>
      <RenglonesDeConteos t={t} ca={ca} idCaja={idCaja} />
    </div>
  );
}

/* ======================================================================
   TABLA POR CARD (una card a la vez; se queda, #548)
   ====================================================================== */

/** t7-3 remate 2 (R7): el ancho (igual para todas) de las columnas de temas de la tabla: el que pide el nombre
    mas largo a dos renglones, o su palabra mas larga (nunca se corta una palabra), entre 96 y 200 px. */
export function anchoDeLosTemas(temas) {
  const nombres = (temas || []).map((tm) => tm.nombre || '');
  const largo = Math.max(0, ...nombres.map((n) => n.length));
  const palabra = Math.max(0, ...nombres.flatMap((n) => n.split(/\s+/)).map((p) => p.length));
  return Math.min(200, Math.max(96, Math.ceil(Math.max(largo / 2, palabra) * 6.6) + 20));
}

/** t7-3 remate 7 (pasada 4, regla 2 de #581): de que lado se ancla el globo de una celda para no salirse de su caja:
    centrado si cabe; si no, pegado al lado de la celda que da hacia dentro de la caja. */
export function ladoDelGlobo({ centro, izquierda, derecha, ancho = 200 }) {
  if (centro + ancho / 2 > derecha) return 'derecha';
  if (centro - ancho / 2 < izquierda) return 'izquierda';
  return 'centro';
}

/** post-t7-1 (A7): el titulo (su caja `r`) esta tapado si empieza por encima del borde de abajo de la barra fija o
    termina por debajo del alto de la ventana. Con un alto de ventana desconocido (0) solo cuenta la barra. */
export function tituloTapado(r, bordeDeLaBarra, altoDeLaVentana) {
  if (!r) return false;
  if (r.top < bordeDeLaBarra) return true;
  return altoDeLaVentana > 0 && r.bottom > altoDeLaVentana;
}

/** post-t7-1 (#596): cuanto dura abierto el letrero de un cuadrito de la tabla. */
export const DURACION_DEL_LETRERO_MS = 3000;

const ANCLA_DEL_GLOBO = {
  centro: { left: '50%', transform: 'translateX(-50%)' },
  derecha: { right: 0, transform: 'none' },
  izquierda: { left: 0, transform: 'none' },
};

function Celda({ estado, titulo, testid, onClick, globo }) {
  const celdaRef = useRef(null);
  const [lado, setLado] = useState('centro');
  useLayoutEffect(() => {
    if (!globo || !celdaRef.current) return;
    // la caja que recorta: el primer ancestro que se desliza de lado (si no hay, la ventana)
    let caja = celdaRef.current.parentElement;
    while (caja && !/(auto|scroll|hidden)/.test(getComputedStyle(caja).overflowX)) caja = caja.parentElement;
    const r = celdaRef.current.getBoundingClientRect();
    const c = caja ? caja.getBoundingClientRect() : { left: 0, right: window.innerWidth };
    const ancho = Math.min(200, window.innerWidth * 0.7);
    setLado(ladoDelGlobo({ centro: (r.left + r.right) / 2, izquierda: c.left, derecha: c.right, ancho }));
  }, [globo]);
  return (
    <td ref={celdaRef} data-celda-del-letrero="si" style={{ textAlign: 'center', position: 'relative', padding: css('8px', '10px') }}>
      {/* t7-3 remate 7 (pasada 4, regla 2 de #581): el cuadro de 26 lleva su area de toque de 44 (medido: entre dos
          cuadros hay 49 px en vertical y 92 en horizontal, asi que no se enciman) */}
      <button type="button" title={titulo} aria-label={titulo} data-estado={estado} data-testid={testid} onClick={onClick}
        style={{ width: 26, height: 26, borderRadius: 7, display: 'inline-block', verticalAlign: 'middle', boxSizing: 'border-box',
          position: 'relative', background: COLOR_DEL_ESTADO[estado],
          ...(estado === 'va_bien' ? borde(VERDE, 2) : (estado === 'poco' ? borde(BORDE_POCO, 2)
            : borde(estado === 'S' ? GRIS_DE_LAS_BARRAS : 'rgba(255,255,255,0.06)'))) }}>
        <AreaDeToque />
      </button>
      {globo && (
        <span role="tooltip" data-testid="avance-globo" data-lado={lado}
          style={{ position: 'absolute', bottom: '100%', ...ANCLA_DEL_GLOBO[lado], zIndex: 30,
            // t7-3 remate 7 (regla 2 de #581): parte renglon en 200 px como mucho (antes un solo renglon que la caja cortaba)
            whiteSpace: 'normal', width: 200, maxWidth: css('min(200px,', '70vw)'), textAlign: 'left', lineHeight: 1.35,
            background: '#262626', color: '#fff', fontSize: 12, ...relleno(4, 8), borderRadius: 6 }}>
          {titulo}
        </span>
      )}
    </td>
  );
}

/** t7-3 remate 3 (B2, #568): en el TELEFONO (ventana de menos de 768) el ancho de cada columna de la tabla, dado
    el espacio visible (la caja menos la columna del alumno) y cuantas columnas hay: si todas caben con 92 px o mas,
    se reparten el espacio; si no, espacio / (n + 0.5) con n el mayor entero (minimo 1) que deje columnas de 92 o
    mas, para que siempre se vean n enteras y la mitad de la siguiente asomando. */
export const MINIMO_COLUMNA_MOVIL = 92;
export function anchosDeLaTablaMovil(visible, columnas, minimo = MINIMO_COLUMNA_MOVIL) {
  const dos = (x) => Math.floor(x * 100) / 100;
  if (columnas > 0 && visible / columnas >= MINIMO_COLUMNA_MOVIL) {
    return { ancho: dos(visible / columnas), reparte: true, enteras: columnas };
  }
  const enteras = Math.max(1, Math.floor(visible / MINIMO_COLUMNA_MOVIL - 0.5));
  // post-t7-1 (A5): con una caja tan angosta que ni una columna y media de 92 cabe (320 px), la cuenta daba columnas de
  // 85; ahi la columna conserva su minimo (`minimo`: 96 las de tema, 92 las de publicacion) y la tabla se desliza
  // dentro de su caja. Desde 375 la caja siempre cabe y la cuenta es la de antes.
  if (visible < MINIMO_COLUMNA_MOVIL * 1.5) return { ancho: Math.max(minimo, dos(visible / (enteras + 0.5))), reparte: false, enteras };
  return { ancho: dos(visible / (enteras + 0.5)), reparte: false, enteras };
}

/** post-t7-1 (A5): el minimo de una columna de TEMA (el mismo piso de `anchoDeLosTemas`). */
export const MINIMO_COLUMNA_DE_TEMA = 96;

/** t7-3 remate 3 (B2): el tope de la columna del alumno (el nombre parte renglon dentro): 220 en computadora y
    118 en el telefono. */
export const TOPE_ALUMNO = { ancho: 220, movil: 118 };

/** t7-3 remate 8 (#587): el ancho tope de una pestana de la tabla (su nombre entero parte renglon DENTRO): 240 en
    computadora y 200 en el telefono. La fila de pestanas es UNA sola y se desliza de lado dentro de su caja. */
export const TOPE_PESTANA = { ancho: 240, movil: 200 };

/** t7-3 remate 8 (#587): cuanto mover la fila (su `scrollLeft`) para que la pestana `[izq, der]` (en coordenadas de
    la fila, sin desplazamiento) quede entera a la vista en una ventana `[0, ancho]` desplazada `actual`; si ya se ve,
    no se mueve. Mueve SOLO el desplazamiento horizontal de la fila (nunca la pagina en vertical). */
export function desplazamientoParaVer({ izq, der, ancho, actual, margen = 8 }) {
  if (izq - margen < actual) return Math.max(0, izq - margen);
  if (der + margen > actual + ancho) return der + margen - ancho;
  return actual;
}

/** post-t7-1 (#595): los estados que aparecen en los cuadritos de la pestana elegida, en el orden de la casa: en el
    Resumen, el de cada alumno en cada publicacion; en una publicacion, el de cada alumno en cada tema. Lee los MISMOS
    datos con que la tabla pinta sus cuadritos (las filas de `orden` y, en una publicacion, sus temas). */
export function estadosPresentesEnLaTabla(orden, card) {
  const vistos = new Set();
  for (const a of orden || []) {
    if (card) {
      const ca = (a.cards || []).find((x) => x.publicacion_id === card.publicacion_id);
      for (const tm of (ca && ca.temas) || []) vistos.add(tm.estado);
    } else {
      for (const ca of a.cards || []) vistos.add(ca.estado);
    }
  }
  return ESTADOS_DE_LA_CARD.filter((e) => vistos.has(e));
}

function Tabla({ t, datos, pestana, setPestana, globo, setGlobo }) {
  const tiraRef = useRef(null);
  const [sombrasTira, setSombrasTira] = useState({ izq: false, der: false });
  const porId = Object.fromEntries((datos.alumnos || []).map((a) => [a.id, a]));
  const orden = (datos.orden_alumnos || []).map((id) => porId[id]).filter(Boolean);
  const cards = datos.cards || [];
  const card = cards.find((c) => c.publicacion_id === pestana) || null;
  // B2: computadora desde 768 de VENTANA (el corte md de la casa); debajo, el telefono
  const esAncho = useMedia(CONSULTA_ANCHO, true);
  const cajaRef = useRef(null);
  const cabeceraRef = useRef(null);
  const [hayMas, setHayMas] = useState(false);
  const [movil, setMovil] = useState(null);
  const columnas = card ? (card.temas || []).length : cards.length;
  const anchoTema = card ? anchoDeLosTemas(card.temas) : 140;
  const presentes = estadosPresentesEnLaTabla(orden, card);

  // B3: la sombra derecha mientras haya mas tabla a la derecha
  const medirSombra = useCallback(() => {
    const caja = cajaRef.current;
    if (!caja) return;
    setHayMas(caja.scrollWidth - caja.scrollLeft - caja.clientWidth > 1);
  }, []);
  // B2: en el telefono, el ancho de cada columna se mide (la caja y la columna del alumno)
  const medirMovil = useCallback(() => {
    const caja = cajaRef.current;
    const cab = cabeceraRef.current;
    if (esAncho || !caja || !cab) { setMovil(null); return; }
    const visible = caja.clientWidth - cab.offsetWidth;
    setMovil(visible > 0 && columnas > 0
      ? anchosDeLaTablaMovil(visible, columnas, card ? MINIMO_COLUMNA_DE_TEMA : MINIMO_COLUMNA_MOVIL) : null);
  }, [esAncho, columnas, card]);
  useLayoutEffect(() => {
    medirMovil();
    medirSombra();
  }, [medirMovil, medirSombra, pestana, datos]);
  useEffect(() => {
    const caja = cajaRef.current;
    if (!caja || typeof ResizeObserver === 'undefined') return undefined;
    const obs = new ResizeObserver(() => { medirMovil(); medirSombra(); });
    obs.observe(caja);
    return () => obs.disconnect();
  }, [medirMovil, medirSombra]);
  useEffect(() => { medirSombra(); });

  const anchoColumna = movil ? movil.ancho : null;
  const fija = (extra = {}) => ({ position: 'sticky', left: 0, background: '#181818', zIndex: 1, textAlign: 'left',
    padding: css('8px', '10px'), borderRightWidth: 1, borderRightStyle: 'solid', borderRightColor: '#262626', ...extra });
  const cab = { fontSize: 12, fontWeight: 600, color: GRIS, padding: css('8px', '10px'), borderBottomWidth: 1,
    borderBottomStyle: 'solid', borderBottomColor: '#262626', verticalAlign: 'bottom' };
  const tope = esAncho ? TOPE_ALUMNO.ancho : TOPE_ALUMNO.movil;
  // una columna de tema (o de publicacion): su nombre ENTERO (parte renglon, nunca corta una palabra)
  const columna = (ancho) => ({ ...cab, width: ancho, minWidth: ancho, maxWidth: ancho, whiteSpace: 'normal',
    overflowWrap: 'normal', wordBreak: 'normal' });
  const anchoDe = (porOmision) => (anchoColumna !== null ? anchoColumna : porOmision);

  const cabeceraDelAlumno = (
    <th ref={cabeceraRef} data-testid="avance-tabla-alumno-cabecera" style={{ ...cab, ...fija({ verticalAlign: 'bottom' }) }}>
      <div style={{ width: 'max-content', maxWidth: tope, whiteSpace: 'normal' }}>
        <span style={{ display: 'block' }}>{t('groups.avance.tabla_alumno')}</span>
        <span data-testid="avance-tabla-alumno-sub"
          style={{ display: 'block', fontSize: 11, fontWeight: 400, color: '#8c8c8c', marginTop: 2 }}>
          {t('groups.avance.tabla_alumno_sub')}
        </span>
      </div>
    </th>
  );
  const celdaDelAlumno = (a) => (
    <td data-testid={`avance-tabla-alumno-${a.id}`} style={fija({ verticalAlign: 'middle' })}>
      <div data-testid={`avance-tabla-alumno-${a.id}-nombre`}
        // t7-3 remate 7 (firma u): mide su nombre hasta el tope y la tabla ya no la aplasta (con muchas columnas largas
        // bajaba a 85 px y el nombre iba en 8 renglones)
        data-ancho="max-content"
        style={{ width: 'max-content', maxWidth: tope, whiteSpace: 'normal', overflowWrap: 'break-word', fontWeight: 600,
          color: BLANCO, fontSize: 13, lineHeight: 1.25 }}>
        {a.nombre}
        <span data-testid={`avance-tabla-alumno-${a.id}-ultima`}
          style={{ display: 'block', fontSize: 12, fontWeight: 400, color: GRIS, marginTop: 2 }}>
          {cuandoTexto(t, a.ultima_practica)}
        </span>
      </div>
    </td>
  );

  // t7-3 remate 8 (#587): la fila de pestanas, sus sombras (hay mas hacia ese lado) y la elegida siempre a la vista
  const medirTira = useCallback(() => {
    const f = tiraRef.current;
    if (!f) return;
    setSombrasTira({ izq: f.scrollLeft > 1, der: f.scrollWidth - f.scrollLeft - f.clientWidth > 1 });
  }, []);
  const verPestana = useCallback((boton) => {
    const f = tiraRef.current;
    if (!f || !boton || !f.clientWidth) return;
    const nuevo = desplazamientoParaVer({ izq: boton.offsetLeft - f.offsetLeft, der: boton.offsetLeft - f.offsetLeft + boton.offsetWidth,
      ancho: f.clientWidth, actual: f.scrollLeft });
    if (nuevo !== f.scrollLeft) f.scrollLeft = nuevo;
    medirTira();
  }, [medirTira]);
  useLayoutEffect(() => {
    const f = tiraRef.current;
    if (f) verPestana(f.querySelector('[aria-pressed="true"]'));
  }, [pestana, verPestana, datos]);
  useEffect(() => {
    const f = tiraRef.current;
    if (!f || typeof ResizeObserver === 'undefined') return undefined;
    const obs = new ResizeObserver(medirTira);
    obs.observe(f);
    return () => obs.disconnect();
  }, [medirTira]);
  const topePestana = esAncho ? TOPE_PESTANA.ancho : TOPE_PESTANA.movil;
  const pestanaDe = (id, texto, { caducada = false, titulo } = {}) => (
    <button key={id} type="button" data-testid={`avance-pestana-${id}`} onClick={() => setPestana(id)} title={titulo}
      onFocus={(e) => verPestana(e.currentTarget)}
      aria-pressed={pestana === id} className="inline-flex items-center"
      style={{ minHeight: 44, paddingTop: 6, paddingBottom: 6, paddingLeft: 13, paddingRight: 13, borderRadius: 18, fontSize: 13,
        fontWeight: 600, gap: 4, textAlign: 'left', lineHeight: 1.3, whiteSpace: 'normal',
        // t7-3 remate 8 (#587): en una fila que se desliza; cada pestana con su nombre ENTERO, partiendo renglon DENTRO de
        // su ancho tope; todas las de la fila miden lo mismo de alto (la fila las estira) con el texto centrado
        flexShrink: 0, maxWidth: topePestana,
        background: pestana === id ? 'rgba(255,107,0,0.12)' : 'transparent',
        ...borde(pestana === id ? NARANJA : '#262626'), color: pestana === id ? NARANJA : GRIS,
        ...(caducada ? { opacity: 0.6 } : {}) }}>
      {caducada && <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />}
      <span style={{ overflowWrap: 'break-word', minWidth: 0 }}>{texto}</span>
    </button>
  );
  // la sombra se funde con el fondo de la seccion (#1a1210)
  const sombra = (lado) => ({ position: 'absolute', top: 0, bottom: 6, width: 26, pointerEvents: 'none', zIndex: 2, [lado]: 0,
    background: lado === 'right'
      ? css('linear-gradient(to', 'right,', 'rgba(26,18,16,0),', 'rgba(26,18,16,0.95))')
      : css('linear-gradient(to', 'left,', 'rgba(26,18,16,0),', 'rgba(26,18,16,0.95))') });
  return (
    <div className="space-y-2">
      {/* LAPIDA t7-3 remate 8 (#587, Carlos: "lo correcto es que se deslice hacia el lado derecho, lo mismo en el
          telefono"): murio "las pestanas parten renglon" (#568). Hoy van en UNA fila que se desliza de lado dentro de su
          caja (la pagina nunca), con la barra fina de la tabla en computadora y una sombra del lado donde hay mas. */}
      <div style={{ position: 'relative' }}>
        <div ref={tiraRef} data-testid="avance-tira" className="flex" onScroll={medirTira}
          data-avance-desliza={esAncho ? 'ancho' : 'telefono'}
          style={{ gap: 8, paddingBottom: 6, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'stretch' }}>
          {pestanaDe('resumen', t('groups.avance.resumen_por_card'))}
          {cards.map((c) => pestanaDe(c.publicacion_id, c.nombre, { caducada: c.caducada, titulo: c.nombre }))}
        </div>
        {sombrasTira.izq && <span aria-hidden="true" data-testid="avance-tira-sombra-izq" style={sombra('left')} />}
        {sombrasTira.der && <span aria-hidden="true" data-testid="avance-tira-sombra-der" style={sombra('right')} />}
      </div>
      {/* LAPIDA post-t7-1 (#595): murio el renglon suelto de "Ha practicado poco" (t7-3 remate 3, #569): ese estado
          ya va en la leyenda de arriba, con los demas. En su lugar, la fila de los estados que de verdad aparecen en los
          cuadritos de la pestana elegida (rotulos, no botones; de los MISMOS datos que pintan los cuadritos). */}
      {presentes.length > 0 && (
        <p data-testid="avance-tabla-leyenda" className="flex flex-wrap items-center"
          style={{ columnGap: 12, rowGap: 4, fontSize: 12, color: GRIS }}>
          {presentes.map((e) => (
            <span key={e} data-testid={`avance-tabla-leyenda-${e}`} className="inline-flex items-center" style={{ gap: 6 }}>
              <Cuadrito estado={e} />{t(`groups.avance.estado.${e}`)}
            </span>
          ))}
        </p>
      )}
      <p data-testid="avance-tabla-nota" style={{ fontSize: 12, color: GRIS }}>
        {t('groups.avance.toca_card')}
      </p>
      {esAncho && <style data-avance-desliza-estilo="si">{ESTILO_DE_LA_BARRA}</style>}
      {/* B1 y B3 (#568): la tabla se desliza de lado DENTRO de su caja (la pagina nunca); la columna del alumno se
          queda fija; la sombra derecha avisa que hay mas; la caja es enfocable para deslizarla con las flechas */}
      <div style={{ ...TARJETA, padding: 0, position: 'relative', overflow: 'hidden' }}>
        <div ref={cajaRef} data-testid="avance-tabla-caja" role="region" tabIndex={0}
          aria-label={t('groups.avance.seccion.tabla')} data-avance-desliza={esAncho ? 'ancho' : 'telefono'}
          onScroll={medirSombra} style={{ overflowX: 'auto' }}>
          {card === null ? (
            <table data-testid="avance-tabla-resumen"
              style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, width: movil && movil.reparte ? '100%' : 'max-content' }}>
              <thead>
                <tr>
                  {cabeceraDelAlumno}
                  {cards.map((c) => (
                    <th key={c.publicacion_id} title={c.nombre} style={columna(anchoDe(140))}>
                      <span className="flex items-center justify-center" style={{ gap: 4 }}>
                        {c.caducada && <Lock className="w-3 h-3 shrink-0" aria-hidden="true" />}
                        <span style={{ textAlign: 'center' }}>{c.nombre}</span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orden.map((a) => (
                  <tr key={a.id} data-testid={`avance-tabla-fila-${a.id}`}>
                    {celdaDelAlumno(a)}
                    {a.cards.map((ca) => {
                      const c = cards.find((x) => x.publicacion_id === ca.publicacion_id) || {};
                      const titulo = `${a.nombre}: ${c.nombre || ''}. ${t(`groups.avance.estado.${ca.estado}`)}`;
                      const clave = `${a.id}|${ca.publicacion_id}`;
                      return (
                        <Celda key={clave} estado={ca.estado} titulo={titulo} testid={`avance-celda-${a.id}-${ca.publicacion_id}`}
                          onClick={() => setGlobo({ clave })} globo={globo && globo.clave === clave} />
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table data-testid={`avance-tabla-card-${card.publicacion_id}`}
              style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, width: movil && movil.reparte ? '100%' : 'max-content' }}>
              <thead>
                <tr>
                  {cabeceraDelAlumno}
                  {card.temas.map((tm) => (
                    <th key={tm.tema_id} title={tm.nombre} style={columna(anchoDe(anchoTema))}>
                      <span style={{ display: 'block', textAlign: 'center' }}>{tm.nombre}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {orden.map((a) => {
                  const ca = a.cards.find((x) => x.publicacion_id === card.publicacion_id) || { temas: [] };
                  const nombres = Object.fromEntries(card.temas.map((tm) => [tm.tema_id, tm.nombre]));
                  return (
                    <tr key={a.id} data-testid={`avance-tabla-fila-${a.id}`}>
                      {celdaDelAlumno(a)}
                      {ca.temas.map((tm) => {
                        const titulo = textoDelCuadro(t, a.nombre, nombres[tm.tema_id] || '', tm);
                        const clave = `${a.id}|${card.publicacion_id}|${tm.tema_id}`;
                        return (
                          <Celda key={clave} estado={tm.estado} titulo={titulo}
                            testid={`avance-celda-${a.id}-${card.publicacion_id}--${tm.tema_id}`}
                            onClick={() => setGlobo({ clave })} globo={globo && globo.clave === clave} />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {hayMas && (
          <span aria-hidden="true" data-testid="avance-tabla-sombra"
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 26, pointerEvents: 'none', zIndex: 2,
              background: css('linear-gradient(to', 'right,', 'rgba(24,24,24,0),', 'rgba(24,24,24,0.9))') }} />
        )}
      </div>
    </div>
  );
}
